import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight, BookOpen, Bot, Check, CheckCircle2,
  ClipboardList, Clock3, Compass, Database, Download, FileText,
  Layers, ListChecks, Loader2, MessageSquareText, Newspaper,
  PanelRight, RefreshCw, Search, Send, Settings2, Sparkles, Square, Target,
  ToggleLeft, ToggleRight, Wand2, Workflow, X, ZoomIn
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { fetchVerifiedVideoZip } from "../../utils/episodeVideoZipDownload";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import { isMockAgentEnabled, setMockAgentEnabled } from "./mockAgentProvider";
import { runTaskToCompletion, startTaskPolling, unwrapTaskResult } from "../../services/taskService";
import { STYLE_OPTIONS } from "../storyboard/common/Constants";
import {
  IMAGE_AI_PROVIDERS,
  TEXT_AI_PROVIDERS,
  normalizeImageProvider,
  normalizeTextProvider,
  toGlobalProviderOption,
} from "../../constants/globalAiProviders";
import {
  DREAMFLY_LLM_MODEL_STORAGE_KEY,
  DREAMFLY_LLM_PROVIDER_ID,
  isDreamflyImageApiModel,
} from "../../constants/dreamflyModels";
import { normalizeVisualStyleKey } from "../../utils/visualStyleReferences";
import {
  getProjectDefaultVideoModel,
  getProjectDefaultVideoRatio,
  getProjectVideoSubmissionProvider,
  isSd25Project,
  isSeedanceVideoProvider,
  normalizeVideoResolutionForProvider,
} from "../../utils/projectRuntime";
import {
  getSeedance25Capabilities,
  normalizeSeedance25Ratio,
  normalizeSeedance25Resolution,
} from "../../utils/seedance25Capabilities";

const SOURCE_TYPES = [
  { id: "hot_news", label: "热点事件", icon: Newspaper },
  { id: "ai_theme", label: "AI生成", icon: Wand2 },
  { id: "user_text", label: "直接输入", icon: FileText },
];

const NOVEL_SOURCE_TYPES = [
  { id: "novel_text", label: "小说导入", icon: BookOpen },
];

const SOURCE_PRESETS = [
  {
    id: "odd-social",
    title: "奇葩社会新闻",
    query: "奇葩社会新闻 反转 热点",
    description: "真实事件、荒诞动机、适合做强钩子的短剧开场。",
  },
  {
    id: "family-dispute",
    title: "家庭纠纷反转",
    query: "家庭纠纷 反转 社会新闻 热点",
    description: "亲情、财产、婚姻冲突，适合连续多集推进。",
  },
  {
    id: "workplace-trap",
    title: "职场与底层逆袭",
    query: "职场纠纷 底层逆袭 社会新闻 反转",
    description: "弱者被误解或被坑害，天然适合爽点和反击线。",
  },
  {
    id: "emotion-bizarre",
    title: "情感奇闻",
    query: "情感纠纷 奇葩新闻 反转 热点",
    description: "爱情、背叛、身份误会，适合情绪传播。",
  },
];

const DEFAULT_SOURCE_ANALYSIS_REQUEST = "请先从短剧改编角度分析这个素材，给我多个可选方向。";
const DEFAULT_NOVEL_ANALYSIS_REQUEST = "请先从小说影视化剧本角度诊断这部小说，判断如何在保留原故事的基础上增强画面感、节奏起伏和单集钩子。";
const ONE_CLICK_EXPLAINER_STATE_KEY = "one_click_explainer";
const DIALOGUE_AUTO_STATE_KEY = "dialogue_short_drama_auto";

const AI_THEME_PRESETS = [
  {
    id: "urban-reversal",
    title: "都市反转爽剧",
    query: "都市底层逆袭 强反转 爽剧",
    description: "适合生成外卖员、保洁、实习生、司机等低身份强反击题材。",
  },
  {
    id: "family-secret",
    title: "家庭秘密",
    query: "家庭伦理 隐瞒身份 财产纠纷 反转",
    description: "适合生成亲情、婚姻、遗产、真假身份类连续冲突。",
  },
  {
    id: "odd-comedy",
    title: "荒诞奇闻",
    query: "荒诞现实 奇葩事件 黑色幽默 反转",
    description: "适合生成离谱开局、误会升级、轻悬疑喜剧题材。",
  },
  {
    id: "suspense-smalltown",
    title: "小镇悬疑",
    query: "小镇秘密 熟人社会 悬疑反转",
    description: "适合生成关系网密集、秘密层层揭开的连续题材。",
  },
];

const LAUNCH_STEPS = [
  { key: "source_understanding", title: "素材理解", desc: "确认素材能不能改，提炼冲突和风险。" },
  { key: "adaptation_options", title: "改编方案", desc: "给出多个短剧方向，由用户单选。" },
  { key: "series_bible", title: "短剧设定", desc: "固定世界观、人物关系和主线。" },
  { key: "style_anchor", title: "风格定调", desc: "选择视觉/视频风格锚点。" },
  { key: "episode_outline", title: "分集大纲", desc: "确认项目体量，开放分集生产台。" },
];

const WORKBENCH_MODES = {
  adaptation: {
    workflowKey: "short_drama_adaptation",
    memoryNamespace: "adaptation_agent_memory",
    sourceTypes: SOURCE_TYPES,
    defaultSourceType: "hot_news",
    defaultSourceQuery: "奇葩社会新闻",
    defaultAnalysisRequest: DEFAULT_SOURCE_ANALYSIS_REQUEST,
    title: "智能改编立项台",
    subtitle: "先立项，再按集生产",
    heroTitle: "建立一个可生产的短剧项目",
    heroDescription: "这里先完成选题、改编方案、短剧设定、风格锚点和分集大纲；单集剧本、分镜和视频提示词会进入分集生产台继续做。",
    sourceTitle: "先确定项目来源",
    sourceHint: "选题只是立项入口，不会直接跳到写单集剧本。",
    textLabel: "粘贴素材",
    textPlaceholder: "粘贴小说、梗概、原作设定或任意素材。",
    textActionLabel: "载入选题",
    selectedSourceTitle: "已确认选题",
    confirmButtonLabel: "确认这个题材",
    candidateTitle: "确认立项素材",
    candidateDescription: "单选一个题材作为项目源头。确认后进入导演对话，再由你发送要求推进素材理解、改编方案、短剧设定、风格锚点和分集大纲。",
    firstInputLabel: "第一步：选题输入",
    sourceComposerHint: "先查找或载入选题；确认后进入导演对话，由你发送要求再开始。",
    chatIntro: "这里是项目立项台：我只负责把素材变成可生产的短剧项目，包括改编方向、短剧设定、风格锚点和分集大纲。立项完成后，再去分集生产台一集一集写剧本、拆分镜和做视频提示词。",
    flowSteps: [
      { id: 1, label: "选题输入" },
      { id: 2, label: "确认题材" },
      { id: 3, label: "立项对话" },
      { id: 4, label: "分集大纲" },
      { id: 5, label: "分集生产" },
    ],
  },
  novel: {
    workflowKey: "novel_to_short_drama",
    memoryNamespace: "novel_agent_memory",
    sourceTypes: NOVEL_SOURCE_TYPES,
    defaultSourceType: "novel_text",
    defaultSourceQuery: "",
    defaultAnalysisRequest: DEFAULT_NOVEL_ANALYSIS_REQUEST,
    title: "小说转剧本工作台",
    subtitle: "导入小说，再按集改写",
    heroTitle: "把小说改写成有画面感的剧本",
    heroDescription: "先导入小说并完成原文诊断、剧本化方向、短剧设定、风格锚点和分集大纲；立项后进入分集生产台，一集一集对话改写剧本。",
    sourceTitle: "先导入小说原文",
    sourceHint: "这一页只固定小说素材，不会直接生成全部剧本。确认后会进入导演对话。",
    textLabel: "粘贴小说",
    textPlaceholder: "粘贴小说正文、章节节选或完整梗概。建议尽量包含连续原文，方便后续按原故事改写成剧本。",
    textActionLabel: "载入小说",
    selectedSourceTitle: "已确认小说",
    confirmButtonLabel: "确认这部小说",
    candidateTitle: "确认小说素材",
    candidateDescription: "确认小说后进入导演对话，再由你发送要求推进小说诊断、剧本化方向、短剧设定、风格锚点和分集大纲。",
    firstInputLabel: "第一步：导入小说",
    sourceComposerHint: "先载入小说；确认后进入导演对话，由你发送要求再开始诊断。",
    chatIntro: "这里是小说转剧本立项台：我先把小说整理成可生产的短剧项目，包括剧本化方向、短剧设定、风格锚点和分集大纲。立项完成后，再去分集生产台一集一集对话改写剧本。",
    flowSteps: [
      { id: 1, label: "导入小说" },
      { id: 2, label: "确认素材" },
      { id: 3, label: "立项对话" },
      { id: 4, label: "分集大纲" },
      { id: 5, label: "分集生产" },
    ],
  },
};

const SURFACE = {
  page: "var(--color-bg-primary)",
  panel: "var(--color-bg-secondary)",
  panelSoft: "var(--color-bg-card)",
  raised: "var(--color-bg-tertiary)",
  input: "var(--color-bg-overlay)",
};

const TONE_STYLE = {
  info: { backgroundColor: "var(--color-blue-light)", color: "var(--color-info)" },
  accent: { backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" },
  success: { backgroundColor: "var(--color-green-light)", color: "var(--color-success)" },
  purple: { backgroundColor: "var(--color-purple-light)", color: "var(--color-purple-primary)" },
  muted: { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" },
};

const STATUS_LABEL = {
  WAITING_USER_REQUEST: "等待指令",
  WAITING_USER_SELECTION: "等待选择",
  CONFIRMED: "已确认",
  FAILED: "失败",
  DRAFT: "草稿",
  RUNNING: "生成中",
  COMPLETED: "已完成",
};

const CONFIRMABLE_RUN_STATUSES = new Set(["WAITING_USER_SELECTION"]);

const runHasError = (run, output) => {
  if (!run) return false;
  if (run.status === "FAILED") return true;
  return Boolean(output?.error);
};

const runCanConfirm = (run, output) => {
  if (!run || runHasError(run, output)) return false;
  return CONFIRMABLE_RUN_STATUSES.has(run.status);
};

const parseMaybeJson = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const getRunOutput = (stageRuns, stageKey) => {
  const runs = (stageRuns || []).filter((run) => run.stageKey === stageKey);
  return runs[runs.length - 1] || null;
};

const optionsFromRun = (run) => {
  const output = parseMaybeJson(run?.outputJson, null);
  return Array.isArray(output?.options) ? output.options : [];
};

const meta = (candidate, key, fallback = "") => candidate?.metadata?.[key] ?? fallback;

const score = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : 0;
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const sourceTypeLabel = (value) => ({
  hot_news: "热点事件",
  ai_theme: "AI生成",
  user_text: "直接输入",
  novel_text: "小说导入",
}[value] || value || "素材");

const isTextSourceType = (value) => value === "user_text" || value === "novel_text";

const sourceActionLabel = (sourceType, config, again = false) => {
  if (sourceType === "ai_theme") return again ? "重新生成" : "生成题材";
  if (sourceType === "hot_news") return again ? "重新搜索" : "查找选题";
  if (sourceType === "novel_text") return again ? "重新载入" : (config?.textActionLabel || "载入小说");
  return again ? "重新载入" : (config?.textActionLabel || "载入素材");
};

const sourceReviewTitle = (sourceType) => {
  if (sourceType === "novel_text") return "小说评估";
  if (isTextSourceType(sourceType)) return "素材评估";
  return "题材评估";
};

const compactDraftText = (value, maxLength = 36) => {
  const text = (value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const sanitizeFileNamePart = (value, fallback = "未命名") => firstText(value, fallback)
  .replace(/[\\/:*?"<>|]/g, "_")
  .replace(/\s+/g, "")
  .replace(/_+/g, "_")
  .slice(0, 80) || fallback;

const downloadHeaders = () => {
  const provider = normalizeTextProvider(localStorage.getItem("ai_provider"));
  const dreamflyLlmModel = provider === DREAMFLY_LLM_PROVIDER_ID
    ? String(localStorage.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || "").trim()
    : "";
  const headers = {
    "X-User-ID": encodeURIComponent(localStorage.getItem("app_user_id") || "default_user"),
    "X-Ai-Provider": provider,
    ...(dreamflyLlmModel && !isDreamflyImageApiModel(dreamflyLlmModel)
      ? { "X-Ai-Model": dreamflyLlmModel }
      : {}),
  };
  const token = localStorage.getItem("app_token") || "";
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const buildEpisodeDownloadLabel = (episode, fallbackEpisodeId) => {
  const order = episode?.sortOrder || episode?.episodeNumber || episode?.episode_number;
  const rawTitle = firstText(episode?.episodeTitle, episode?.title, episode?.name, fallbackEpisodeId ? `第${fallbackEpisodeId}集` : "当前集");
  if (!order) return rawTitle;
  const cleanTitle = rawTitle
    .replace(new RegExp(`^第\\s*${order}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
  return cleanTitle ? `第${order}集_${cleanTitle}` : `第${order}集`;
};

const isAutoVideoDone = (row) => {
  const status = firstText(row?.status, row?.taskStatus, row?.currentStage).toLowerCase();
  if (status.includes("failed") || status.includes("error") || status.includes("canceled")) return false;
  return Boolean(firstText(row?.videoUrl, row?.url, row?.resultUrl))
    || ["skipped_existing_video", "video_done", "video_success", "done", "success", "completed"].includes(status);
};

const countAutoWorkflowVideos = (episodes) => {
  const videoRows = asArray(episodes).flatMap((episode) => asArray(episode?.videos || episode?.videoResults));
  return {
    done: videoRows.filter(isAutoVideoDone).length,
    total: videoRows.length,
  };
};

const sourceDraftTitle = (sourceType, sourceQuery = "", sourceText = "") => {
  const seed = compactDraftText(isTextSourceType(sourceType) ? sourceText : sourceQuery);
  const prefix = {
    hot_news: "热点",
    ai_theme: "AI题材",
    user_text: "文本",
    novel_text: "小说",
  }[sourceType] || "智能改编";
  if (seed) return `${prefix}：${seed}`;
  return {
    hot_news: "热点改编草稿",
    ai_theme: "AI题材草稿",
    user_text: "文本改编草稿",
    novel_text: "小说转剧本草稿",
  }[sourceType] || "智能改编草稿";
};

const adaptationMemoryKey = (projectId, namespace = "adaptation_agent_memory") => `${namespace}_${projectId || "global"}`;
const MAX_MEMORY_TEXT = 4000;

const trimForMemory = (value, maxLength = MAX_MEMORY_TEXT) => {
  if (typeof value !== "string") return value;
  return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const candidatesForMemory = (sourceType, candidates) => {
  if (sourceType === "novel_text") return [];
  return (candidates || []).map((candidate) => {
    const metadata = candidate?.metadata ? { ...candidate.metadata } : {};
    if (typeof metadata.full_text === "string") metadata.full_text = trimForMemory(metadata.full_text, 1200);
    return {
      ...candidate,
      summary: trimForMemory(candidate?.summary, 1200),
      fullText: trimForMemory(candidate?.fullText, 1200),
      metadata,
    };
  });
};

const readAdaptationMemory = (projectId, namespace) => {
  try {
    const raw = window.localStorage.getItem(adaptationMemoryKey(projectId, namespace));
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const writeAdaptationMemory = (projectId, patch, namespace) => {
  try {
    const current = readAdaptationMemory(projectId, namespace);
    window.localStorage.setItem(adaptationMemoryKey(projectId, namespace), JSON.stringify({ ...current, ...patch }));
  } catch {
    // 本地记忆失败不影响主流程。
  }
};

const candidateStoryLine = (candidate) => firstText(
  meta(candidate, "one_sentence_story"),
  meta(candidate, "logline"),
  meta(candidate, "short_drama_hook"),
  candidate?.summary,
);

const materialStoryLine = (material) => firstText(
  material?.metadata?.one_sentence_story,
  material?.metadata?.logline,
  material?.metadata?.short_drama_hook,
  material?.summary,
);

const humanLabel = (key) => {
  const labels = {
    id: "编号",
    key: "类型",
    title: "标题",
    name: "名称",
    summary: "摘要",
    description: "说明",
    sourceType: "素材来源",
    fullText: "完整文本",
    sourceRefs: "来源记录",
    metadata: "附加信息",
    projectId: "项目",
    workflowKey: "创作流程",
    currentStageKey: "当前阶段",
    stageKey: "阶段",
    artifactType: "物料类型",
    version: "版本",
    parentVersion: "父版本",
    selected: "已选定",
    status: "状态",
    createdAt: "创建时间",
    updatedAt: "更新时间",
    source_material: "素材",
    project_context: "项目资料",
    user_feedback: "用户意见",
    stage_history: "阶段历史",
    confirmed_artifacts: "已确认物料",
    workflow_state: "创作状态",
    finalContext: "最终参考资料",
    packets: "资料包",
    tokenEstimate: "预估消耗",
    priority: "优先级",
    contentType: "内容类型",
    visibility: "可见范围",
    tokenBudget: "预算",
    compressible: "可压缩",
    premise: "故事前提",
    logline: "一句话钩子",
    one_sentence_story: "一句话故事",
    source_summary: "素材摘要",
    theme: "主题",
    genre: "类型",
    tone: "气质",
    conflict: "核心冲突",
    core_conflict: "核心冲突",
    core_conflicts: "核心冲突",
    world_setting: "世界设定",
    characters: "人物",
    character: "人物",
    aliases: "别名",
    role: "角色定位",
    personality: "性格",
    arc: "人物弧光",
    visual_keywords: "外形关键词",
    visual_style_anchor: "视觉风格锚点",
    video_style_anchor: "视频风格锚点",
    style_candidates: "风格候选",
    selected_style_id: "已选风格",
    selected_candidate: "已选候选",
    selected_image_url: "锚点参考图",
    image_url: "参考图",
    style_prompt: "固定生图风格词",
    negative_prompt: "反向提示词",
    fixed_source: "固定来源",
    era_country: "年代地域",
    wardrobe_makeup: "服装妆造",
    overall_style: "整体视觉风格",
    color_palette: "色彩锚点",
    lighting: "光线锚点",
    lens_composition: "镜头构图",
    texture: "画面质感",
    consistency_rules: "统一规则",
    negative_constraints: "避免事项",
    motion_style: "运镜风格",
    pacing: "视频节奏",
    camera_language: "镜头语言",
    editing_rhythm: "剪辑节奏",
    performance_direction: "表演方向",
    model_prompt_rules: "创作提示词规则",
    visual_profile: "五官体态表",
    body_proportion: "身体比例",
    face_shape: "脸型",
    bone_structure: "骨相",
    brow_eye: "眉眼",
    nose: "鼻子",
    lips_chin_jaw: "唇下巴下颌",
    beauty_type: "美感类型",
    makeup_hair: "妆发",
    visual_anchors: "稳定识别点",
    scenes: "场景",
    props: "道具",
    episodes: "剧集",
    episode: "剧集",
    episode_count: "集数",
    episode_number: "集数",
    episodeTitle: "剧集标题",
    opening_hook: "开场钩子",
    core_hook: "核心钩子",
    episode_potential: "多集潜力",
    twist: "反转",
    twist_strategy: "反转策略",
    cliffhanger: "结尾悬念",
    script: "剧本文本",
    shots: "分镜",
    storyboard: "分镜",
    shot_number: "镜头编号",
    shot: "景别",
    shotTitle: "镜头标题",
    visual_content: "画面内容",
    content: "对白/内容",
    audio_desc: "声音说明",
    action: "动作/运镜",
    video_prompts: "视频提示词",
    image_prompt: "生图提示词",
    video_prompt: "视频提示词",
    seedance_prompt: "即梦提示词",
    director_enabled: "导演介入",
    director_context: "导演建议",
    risks: "风险",
    risk_notes: "风险提示",
    adaptation_risks: "改编风险",
    pros: "优点",
    recommended: "推荐",
    questions_for_user: "需要确认的问题",
    agent_questions: "导演问题",
    why_selected: "选择理由",
    short_drama_hook: "短剧钩子",
    adaptation_potential: "改编潜力",
    source: "来源",
    url: "链接",
    publishedAt: "发布时间",
    raw_text: "原始内容",
  };
  return labels[key] || "补充信息";
};

const valueTitle = (value, fallback = "内容") => {
  if (!value || typeof value !== "object") return fallback;
  return value.title || value.name || value.episodeTitle || value.shotTitle || fallback;
};

const optionDisplayName = (options, optionId) => {
  if (!optionId) return "";
  const index = (options || []).findIndex((option) => String(option.id) === String(optionId));
  if (index < 0) return "已选方案";
  return options[index]?.title || `方案 ${index + 1}`;
};

const stageGuide = (stageKey, stageName = "当前阶段") => {
  const guides = {
    source_understanding: {
      title: "素材理解",
      goal: "我会先把素材拆成可改编的冲突、风险和几个理解角度。",
      prompt: "可以补充你想保留的真实细节、避开的敏感点，或者直接发送让我分析素材。",
      cta: "发送后会生成多个理解角度，你再选择或要求重想。",
    },
    adaptation_options: {
      title: "创作方向",
      goal: "这一阶段会把已确认的素材整理成 3 到 5 个可选剧本方向。",
      prompt: "可以告诉导演你想要的表达重心、节奏、保留内容或风格偏好，或者直接发送。",
      cta: "发送后会出现可单选的方向方案。",
    },
    series_bible: {
      title: "短剧设定",
      goal: "这一阶段会生成短剧名、一句话卖点、世界设定、核心冲突和主要人物。",
      prompt: "可以补充主角身份、反派强度、人物关系、结局气质，或者直接发送生成短剧设定。",
      cta: "发送后会得到一份可阅读的短剧设定文档。",
    },
    style_anchor: {
      title: "风格定调",
      goal: "这一阶段会先生成多张风格探索图，帮你选择整部短剧的视觉和视频锚点。",
      prompt: "可以补充真人/动漫、年代、国家地域、光影、妆造、参考作品气质，或者直接发送生成风格图。",
      cta: "发送后会出现可单选的风格参考图；选中后确认，系统会反推并固定风格提示词。",
    },
    episode_outline: {
      title: "分集大纲",
      goal: "这一阶段会把短剧设定拆成适合当前项目的分集大纲，每集都有钩子、冲突、反转和悬念。",
      prompt: "可以指定目标集数、节奏、前几集爆点、结尾方式，或者直接发送生成大纲。",
      cta: "发送后会得到分集大纲。",
    },
    episode_script: {
      title: "分集剧本",
      goal: "这一阶段会把大纲逐集扩成剧本文本，你可以看完一集、修改一集，再继续下一集。",
      prompt: "默认生成下一集；也可以指定：生成3集、第5-7集、修改第4集、追加第21集。",
      cta: "写完至少一集就可以先进入后续流程；后续回到这里也可以继续补新集或修改旧集。",
    },
    asset_breakdown: {
      title: "资产拆分",
      goal: "这一阶段会拆出人物、场景、道具，并给工坊准备生成提示词。",
      prompt: "可以强调人物五官风格、服装时代、场景质感，或者直接发送拆分资产。",
      cta: "确认这一阶段后会自动同步到人物、场景、道具工坊。",
    },
    storyboard: {
      title: "分镜草稿",
      goal: "这一阶段会把剧本拆成镜头，包含画面、对白、动作、生图提示词和视频提示词。",
      prompt: "可以指定镜头密度、景别偏好、是否更适合视频演绎模式，或者直接发送生成分镜。",
      cta: "发送后可以导入分镜草稿。",
    },
    video_prompt: {
      title: "视频提示词优化",
      goal: "这一阶段会把分镜里的视频提示词优化成更适合视频生成平台执行的版本。",
      prompt: "可以指定平台偏好、动作强度、运镜风格，或者直接发送优化视频提示词。",
      cta: "发送后会得到按镜头整理的视频提示词。",
    },
  };
  return guides[stageKey] || {
    title: stageName,
    goal: `这一阶段会推进「${stageName}」。`,
    prompt: "可以告诉导演你的要求，或者不写补充，直接发送。",
    cta: "发送后会生成当前阶段产物。",
  };
};

const isPrimitive = (value) => value == null || ["string", "number", "boolean"].includes(typeof value);

const KNOWN_VALUE_LABELS = {
  short_drama_adaptation: "短剧改编流程",
  novel_to_short_drama: "小说转短剧流程",
  hot_news: "热点事件",
  ai_theme: "AI生成",
  user_text: "直接输入",
  novel_text: "小说导入",
  source_material: "素材",
  project_context: "项目资料",
  user_feedback: "用户意见",
  stage_history: "阶段历史",
  confirmed_artifacts: "已确认物料",
  workflow_state: "创作状态",
  source_understanding: "素材理解",
  adaptation_options: "改编方案",
  series_bible: "短剧设定",
  style_anchor: "风格定调",
  episode_outline: "分集大纲",
  episode_script: "分集剧本",
  asset_breakdown: "资产拆分",
  storyboard: "分镜草稿",
  video_prompt: "视频提示词优化",
  SOURCE_MATERIAL: "素材",
  SOURCE_UNDERSTANDING: "素材理解",
  ADAPTATION_OPTION: "改编方案",
  SERIES_BIBLE: "短剧设定",
  STYLE_ANCHOR: "风格锚点",
  EPISODE_OUTLINE: "分集大纲",
  EPISODE_SCRIPT: "分集剧本",
  ASSET_BREAKDOWN: "资产拆分",
  STORYBOARD_DRAFT: "分镜草稿",
  VIDEO_PROMPT_DRAFT: "视频提示词草稿",
  WAITING_USER_REQUEST: "等待指令",
  WAITING_USER_SELECTION: "等待选择",
  CONFIRMED: "已确认",
  FAILED: "失败",
  DRAFT: "草稿",
  RUNNING: "生成中",
  COMPLETED: "已完成",
  raw_output: "原始输出",
  json: "结构化内容",
  model: "创作引擎",
  ui: "界面",
  audit: "审计",
};

const displayPrimitive = (value) => {
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "string") {
    if (KNOWN_VALUE_LABELS[value]) return KNOWN_VALUE_LABELS[value];
    const optionMatch = value.match(/^(?:angle|option)_(\d+)$/);
    if (optionMatch) return `方案 ${optionMatch[1]}`;
  }
  return String(value);
};

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter((item) => item != null && item !== "") : [value];
};

const positiveInteger = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
};

const latestArtifactContent = (artifacts, artifactType) => {
  const matched = (artifacts || []).filter((artifact) => artifact.artifactType === artifactType);
  const selected = [...matched].reverse().find((artifact) => artifact.selected);
  const latest = selected || matched[matched.length - 1];
  return latest ? parseMaybeJson(latest.contentJson, {}) : {};
};

const episodeTextReady = (episode) => firstText(
  episode?.script,
  episode?.script_content,
  episode?.screenplay,
);

const episodeScriptCompletion = (runOutput, artifacts) => {
  if (!runOutput || typeof runOutput !== "object") {
    return {
      complete: false,
      expectedCount: 0,
      readyCount: 0,
      missingNumbers: [],
      message: "还没有生成分集剧本。",
    };
  }

  const outline = latestArtifactContent(artifacts, "EPISODE_OUTLINE");
  const outlineEpisodes = asArray(outline?.episodes);
  const outputExpectedCount = positiveInteger(runOutput?.episode_count) || positiveInteger(runOutput?.batch_info?.expected_count);
  let maxEpisodeNumber = 0;
  const readyNumbers = new Set();

  asArray(runOutput.episodes).forEach((episode, index) => {
    const number = positiveInteger(episode?.episode_number) || index + 1;
    maxEpisodeNumber = Math.max(maxEpisodeNumber, number);
    if (number > 0 && episodeTextReady(episode)) {
      readyNumbers.add(number);
    }
  });

  const expectedCount = Math.max(
    positiveInteger(outline?.episode_count) || 0,
    outlineEpisodes.length,
    outputExpectedCount || 0,
    maxEpisodeNumber,
  );

  const missingNumbers = [];
  for (let number = 1; number <= expectedCount; number += 1) {
    if (!readyNumbers.has(number)) missingNumbers.push(number);
  }

  const readyCount = expectedCount - missingNumbers.length;
  const missingPreview = missingNumbers.slice(0, 8).map((number) => `第${number}集`).join("、");
  const overflow = missingNumbers.length > 8 ? `等 ${missingNumbers.length} 集` : "";
  const complete = expectedCount > 0 && missingNumbers.length === 0;

  return {
    complete,
    expectedCount,
    readyCount,
    missingNumbers,
    message: expectedCount <= 0
      ? "还没有确定分集数量，请先生成分集大纲。"
      : complete
      ? `分集剧本已生成完整：${readyCount}/${expectedCount} 集。`
      : `分集剧本进度：当前 ${readyCount}/${expectedCount} 集，下一步可继续写 ${missingPreview || "下一集"}${overflow}。`,
  };
};

const pickText = (value, fallback = "") => {
  if (value == null) return fallback;
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number" || typeof value === "boolean") return displayPrimitive(value);
  return fallback;
};

const MarkdownSection = ({ title, children }) => {
  if (!children) return null;
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--color-accent-primary)" }} />
        <h4 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h4>
      </div>
      <div className="pl-3 border-l space-y-2" style={{ borderColor: "var(--color-border-primary)" }}>
        {children}
      </div>
    </section>
  );
};

const MarkdownParagraph = ({ children, muted = false }) => {
  if (!children) return null;
  return (
    <p className="text-sm leading-7 whitespace-pre-wrap" style={{ color: muted ? "var(--color-text-muted)" : "var(--color-text-secondary)" }}>
      {children}
    </p>
  );
};

const MarkdownQuote = ({ children }) => {
  if (!children) return null;
  return (
    <div className="rounded-md border px-3 py-2 text-sm leading-7" style={{ borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)", color: "var(--color-text-primary)" }}>
      {children}
    </div>
  );
};

const BulletList = ({ items }) => {
  const list = asArray(items).map((item) => pickText(item)).filter(Boolean);
  if (!list.length) return null;
  return (
    <ul className="space-y-1">
      {list.slice(0, 12).map((item, index) => (
        <li key={`${item}-${index}`} className="flex gap-2 text-sm leading-7" style={{ color: "var(--color-text-secondary)" }}>
          <span className="mt-3 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: "var(--color-text-muted)" }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const InlineMeta = ({ items }) => {
  const list = (items || []).filter((item) => item?.value != null && item.value !== "");
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((item) => (
        <span key={item.label} className="px-2 py-1 rounded text-sm" style={{ backgroundColor: SURFACE.raised, color: "var(--color-text-secondary)" }}>
          <span style={{ color: "var(--color-text-muted)" }}>{item.label}：</span>{displayPrimitive(item.value)}
        </span>
      ))}
    </div>
  );
};

const roleVisualStyle = (role) => {
  const text = String(role || "");
  if (/反派|敌人|对手|恶人|坏人|黑手/.test(text)) {
    return {
      card: { borderColor: "var(--color-error)", backgroundColor: "var(--color-red-light)" },
      badge: { borderColor: "var(--color-error)", backgroundColor: "var(--color-red-light)", color: "var(--color-error)" },
    };
  }
  if (/主角|女主|男主|主人公|主线/.test(text)) {
    return {
      card: { borderColor: "var(--color-success)", backgroundColor: "var(--color-green-light)" },
      badge: { borderColor: "var(--color-success)", backgroundColor: "var(--color-green-light)", color: "var(--color-success)" },
    };
  }
  if (/配角|群像|路人|支线/.test(text)) {
    return {
      card: { borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft },
      badge: { borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" },
    };
  }
  return {
    card: { borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft },
    badge: { borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" },
  };
};

const DetailCard = ({ title, subtitle, children, tone = "default", cardStyle, badgeStyle }) => {
  const toneStyle = tone === "accent"
    ? { borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)" }
    : { borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft };
  return (
    <article className="rounded-md border p-3 space-y-2" style={{ ...toneStyle, ...cardStyle }}>
      {(title || subtitle) && (
        <div className="flex items-start justify-between gap-3">
          {title && <h5 className="text-sm font-semibold leading-6" style={{ color: "var(--color-text-primary)" }}>{title}</h5>}
          {subtitle && (
            <span
              className="text-sm shrink-0 rounded px-2 py-0.5 border"
              style={badgeStyle || { borderColor: "transparent", color: "var(--color-text-muted)" }}
            >
              {subtitle}
            </span>
          )}
        </div>
      )}
      {children}
    </article>
  );
};

const VisualProfile = ({ profile }) => {
  if (!profile || typeof profile !== "object") return null;
  const entries = [
    ["身体比例", profile.body_proportion],
    ["脸型", profile.face_shape],
    ["骨相", profile.bone_structure],
    ["眉眼", profile.brow_eye],
    ["鼻子", profile.nose],
    ["唇下巴下颌", profile.lips_chin_jaw],
    ["美感类型", profile.beauty_type],
    ["妆发", profile.makeup_hair],
    ["稳定识别点", asArray(profile.visual_anchors).map((item) => pickText(item)).join("，")],
  ].filter(([, value]) => value);
  if (!entries.length) return null;
  return (
    <div className="rounded-md p-2.5 space-y-1.5" style={{ backgroundColor: SURFACE.raised }}>
      <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>五官体态表</div>
      {entries.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-sm leading-6">
          <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
          <span style={{ color: "var(--color-text-secondary)" }}>{value}</span>
        </div>
      ))}
    </div>
  );
};

const StyleAnchorCard = ({ title, anchor, tone = "accent" }) => {
  if (!anchor || typeof anchor !== "object") return null;
  const entries = Object.entries(anchor)
    .filter(([, value]) => value != null && value !== "" && (!Array.isArray(value) || value.length));
  if (!entries.length) return null;
  const cardStyle = tone === "video"
    ? { borderColor: "var(--color-info)", backgroundColor: "var(--color-blue-light)" }
    : undefined;
  const badgeStyle = tone === "video"
    ? { borderColor: "var(--color-info)", color: "var(--color-info)", backgroundColor: "var(--color-blue-light)" }
    : undefined;
  return (
    <DetailCard title={title} subtitle={tone === "video" ? "视频统一" : "画面统一"} tone={tone === "video" ? "default" : "accent"} cardStyle={cardStyle} badgeStyle={badgeStyle}>
      <div className="space-y-2">
        {entries.map(([key, value]) => (
          <div key={key} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 text-sm leading-6">
            <span className="font-semibold" style={{ color: "var(--color-text-muted)" }}>{humanLabel(key)}</span>
            <div style={{ color: "var(--color-text-secondary)" }}>
              {Array.isArray(value) ? <BulletList items={value} /> : isPrimitive(value) ? displayPrimitive(value) : <GenericMarkdown value={value} depth={1} />}
            </div>
          </div>
        ))}
      </div>
    </DetailCard>
  );
};

const StyleAnchors = ({ output }) => {
  if (!output?.visual_style_anchor && !output?.video_style_anchor) return null;
  return (
    <MarkdownSection title="风格锚点">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
        <StyleAnchorCard title="视觉风格锚点" anchor={output.visual_style_anchor} />
        <StyleAnchorCard title="视频风格锚点" anchor={output.video_style_anchor} tone="video" />
      </div>
    </MarkdownSection>
  );
};

const StyleImagePreviewModal = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [image, onClose]);

  if (!image) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-5 animate-in fade-in duration-150"
      style={{ backgroundColor: "var(--overlay-heavy)" }}
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="风格参考图预览"
    >
      <div
        className="relative max-w-[92vw] max-h-[88vh] rounded-md border overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-card)" }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 w-9 h-9 rounded-md border flex items-center justify-center cursor-pointer"
          style={{ borderColor: "var(--color-border-primary)", backgroundColor: "color-mix(in srgb, var(--color-bg-card) 88%, transparent)", color: "var(--color-text-primary)" }}
          aria-label="关闭预览"
        >
          <X size={18} />
        </button>
        <img
          src={image.url}
          alt={image.title || "风格参考图"}
          className="block max-w-[92vw] max-h-[88vh] object-contain"
        />
        {image.title && (
          <div
            className="absolute left-0 right-0 bottom-0 px-4 py-3 text-sm font-semibold"
            style={{ backgroundColor: "var(--overlay-medium)", color: "white" }}
          >
            {image.title}
          </div>
        )}
      </div>
    </div>
  );
};

const StyleImageThumb = ({ imageUrl, title, onPreview }) => {
  if (!imageUrl) return null;
  return (
    <div className="relative rounded-md overflow-hidden border group" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}>
      <img src={imageUrl} alt={title || "风格参考图"} className="w-full object-cover" style={{ aspectRatio: "16 / 9" }} />
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onPreview?.({ url: imageUrl, title });
        }}
        onKeyDown={(event) => event.stopPropagation()}
        className="absolute right-2 top-2 h-8 px-2.5 rounded-md border flex items-center gap-1.5 text-sm font-semibold cursor-pointer transition-opacity"
        style={{
          borderColor: "var(--color-border-primary)",
          backgroundColor: "color-mix(in srgb, var(--color-bg-card) 88%, transparent)",
          color: "var(--color-text-primary)",
        }}
        aria-label="放大风格参考图"
      >
        <ZoomIn size={14} />
        放大
      </button>
    </div>
  );
};

const AgentMotionStyles = () => (
  <style>{`
    @keyframes adaptation-pop-in {
      from { opacity: 0; transform: translateY(10px) scale(0.985); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    .agent-motion-card {
      animation: adaptation-pop-in 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
      transition: transform 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease, opacity 180ms ease;
    }

    .agent-motion-card:hover {
      transform: translateY(-1px);
      box-shadow: var(--shadow-card);
    }

    .agent-motion-message {
      animation: adaptation-pop-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
    }

    @media (prefers-reduced-motion: reduce) {
      .agent-motion-card,
      .agent-motion-message {
        animation: none;
        transition: none;
      }
      .agent-motion-card:hover {
        transform: none;
        box-shadow: none;
      }
    }
  `}</style>
);

const StyleAnchorFixedSummary = ({ output }) => {
  const [previewImage, setPreviewImage] = useState(null);
  if (!output) return null;
  const imageUrl = firstText(output.selected_image_url, output.visual_style_anchor?.selected_image_url, output.selected_candidate?.image_url);
  const title = firstText(output.selected_candidate?.title, output.title, "已固定风格锚点");
  const hasAnchor = output.visual_style_anchor || output.video_style_anchor || output.style_prompt || imageUrl;
  if (!hasAnchor) return null;
  return (
    <div className="space-y-4">
      <MarkdownSection title="已选风格">
        <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-3">
          {imageUrl && (
            <StyleImageThumb imageUrl={imageUrl} title={title} onPreview={setPreviewImage} />
          )}
          <div className="space-y-2">
            <div className="text-lg font-semibold leading-7" style={{ color: "var(--color-text-primary)" }}>{title}</div>
            <MarkdownParagraph>{firstText(output.selected_candidate?.summary, output.summary)}</MarkdownParagraph>
            {output.style_prompt && <MarkdownParagraph muted>{`固定生图风格词：${output.style_prompt}`}</MarkdownParagraph>}
            {output.negative_prompt && <MarkdownParagraph muted>{`反向提示词：${output.negative_prompt}`}</MarkdownParagraph>}
          </div>
        </div>
      </MarkdownSection>
      <StyleAnchors output={output} />
      <StyleImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />
    </div>
  );
};

const GenericMarkdown = ({ value, depth = 0 }) => {
  if (value == null || value === "") return null;
  if (isPrimitive(value)) return <MarkdownParagraph>{displayPrimitive(value)}</MarkdownParagraph>;
  if (Array.isArray(value)) {
    if (!value.length) return null;
    if (value.every(isPrimitive)) return <BulletList items={value} />;
    return (
      <div className="space-y-2">
        {value.slice(0, 8).map((item, index) => (
          <DetailCard key={index} title={valueTitle(item, `条目 ${index + 1}`)}>
            <GenericMarkdown value={item} depth={depth + 1} />
          </DetailCard>
        ))}
      </div>
    );
  }
  const entries = Object.entries(value)
    .filter(([key, item]) => item != null && item !== "" && key !== "options" && key !== "id")
    .slice(0, depth > 1 ? 8 : 14);
  if (!entries.length) return null;
  return (
    <div className="space-y-2">
      {entries.map(([key, item]) => (
        <div key={key} className="space-y-1">
          <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{humanLabel(key)}</div>
          <GenericMarkdown value={item} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
};

const MarkdownOutput = ({ output }) => {
  if (!output) return null;
  if (output.raw_text) {
    return (
      <MarkdownSection title="原始内容">
        <MarkdownParagraph>{output.raw_text}</MarkdownParagraph>
      </MarkdownSection>
    );
  }

  const episodes = asArray(output.episodes);
  const characters = asArray(output.characters);
  const scenes = asArray(output.scenes);
  const props = asArray(output.props);
  const storyboardEpisodes = episodes.filter((episode) => Array.isArray(episode.storyboard));
  const videoEpisodes = episodes.filter((episode) => Array.isArray(episode.shots));

  if (output.selected_style_id || output.selected_candidate || output.style_candidates) {
    return <StyleAnchorFixedSummary output={output} />;
  }

  if (output.title || output.logline || output.world_setting || output.core_conflict || output.twist_strategy) {
    return (
      <div className="space-y-4">
        <MarkdownQuote>{firstText(output.logline, output.premise, output.core_hook)}</MarkdownQuote>
        <InlineMeta items={[
          { label: "类型", value: output.genre },
          { label: "气质", value: output.tone },
          { label: "集数", value: output.episode_count },
        ]} />
        <MarkdownSection title="核心冲突">
          <MarkdownParagraph>{firstText(output.core_conflict, output.conflict)}</MarkdownParagraph>
        </MarkdownSection>
        <MarkdownSection title="世界设定">
          <MarkdownParagraph>{output.world_setting}</MarkdownParagraph>
        </MarkdownSection>
        {!!characters.length && (
          <MarkdownSection title="人物">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
              {characters.slice(0, 8).map((character, index) => {
                const roleStyle = roleVisualStyle(character.role);
                return (
                  <DetailCard
                    key={`${character.name || index}`}
                    title={character.name || `人物 ${index + 1}`}
                    subtitle={character.role || "配角"}
                    cardStyle={roleStyle.card}
                    badgeStyle={roleStyle.badge}
                  >
                    <MarkdownParagraph>{firstText(character.description, character.personality, character.arc)}</MarkdownParagraph>
                    <InlineMeta items={[
                      { label: "性格", value: character.personality },
                      { label: "弧光", value: character.arc },
                    ]} />
                    <VisualProfile profile={character.visual_profile} />
                    {character.visual_keywords && <MarkdownParagraph muted>{character.visual_keywords}</MarkdownParagraph>}
                  </DetailCard>
                );
              })}
            </div>
          </MarkdownSection>
        )}
        <MarkdownSection title="反转策略">
          <MarkdownParagraph>{output.twist_strategy}</MarkdownParagraph>
        </MarkdownSection>
        <MarkdownSection title="风险提示">
          <BulletList items={output.risk_notes || output.risks || output.adaptation_risks} />
        </MarkdownSection>
      </div>
    );
  }

  if (output.source_summary || output.core_conflicts || output.adaptation_risks || output.questions_for_user) {
    return (
      <div className="space-y-4">
        <MarkdownSection title="素材摘要">
          <MarkdownParagraph>{output.source_summary || output.summary}</MarkdownParagraph>
        </MarkdownSection>
        <MarkdownSection title="核心冲突">
          <BulletList items={output.core_conflicts} />
        </MarkdownSection>
        <MarkdownSection title="改编风险">
          <BulletList items={output.adaptation_risks || output.risks} />
        </MarkdownSection>
        <MarkdownSection title="需要确认">
          <BulletList items={output.questions_for_user || output.agent_questions} />
        </MarkdownSection>
      </div>
    );
  }

  if (storyboardEpisodes.length) {
    return (
      <div className="space-y-4">
        <StyleAnchors output={output} />
        {storyboardEpisodes.slice(0, 6).map((episode, episodeIndex) => (
          <MarkdownSection key={episodeIndex} title={`第 ${episode.episode_number || episodeIndex + 1} 集分镜`}>
            <div className="space-y-2">
              {asArray(episode.storyboard).slice(0, 10).map((shot, shotIndex) => (
                <DetailCard key={shotIndex} title={`镜头 ${shot.shot_number || shotIndex + 1}`} subtitle={shot.shot}>
                  <MarkdownParagraph>{firstText(shot.visual_content, shot.content)}</MarkdownParagraph>
                  <InlineMeta items={[
                    { label: "动作/运镜", value: shot.action },
                    { label: "声音", value: shot.audio_desc },
                  ]} />
                  {shot.image_prompt && <MarkdownParagraph muted>{`生图：${shot.image_prompt}`}</MarkdownParagraph>}
                  {shot.video_prompt && <MarkdownParagraph muted>{`视频：${shot.video_prompt}`}</MarkdownParagraph>}
                </DetailCard>
              ))}
            </div>
          </MarkdownSection>
        ))}
      </div>
    );
  }

  if (videoEpisodes.length) {
    return (
      <div className="space-y-4">
        <StyleAnchors output={output} />
        {videoEpisodes.slice(0, 6).map((episode, episodeIndex) => (
          <MarkdownSection key={episodeIndex} title={`第 ${episode.episode_number || episodeIndex + 1} 集视频创作提示`}>
            <div className="space-y-2">
              {asArray(episode.shots).slice(0, 10).map((shot, shotIndex) => (
                <DetailCard key={shotIndex} title={`镜头 ${shot.shot_number || shotIndex + 1}`}>
                  <MarkdownParagraph>{shot.video_prompt}</MarkdownParagraph>
                  {shot.seedance_prompt && <MarkdownParagraph muted>{`参考演戏：${shot.seedance_prompt}`}</MarkdownParagraph>}
                </DetailCard>
              ))}
            </div>
          </MarkdownSection>
        ))}
      </div>
    );
  }

  if (episodes.length) {
    return (
      <div className="space-y-4">
        <InlineMeta items={[{ label: "集数", value: output.episode_count || episodes.length }]} />
        <MarkdownSection title="分集内容">
          <div className="space-y-2">
            {episodes.slice(0, 20).map((episode, index) => (
              <DetailCard key={index} title={episode.title || `第 ${episode.episode_number || index + 1} 集`} subtitle={`第 ${episode.episode_number || index + 1} 集`}>
                <MarkdownParagraph>{firstText(episode.summary, episode.script)}</MarkdownParagraph>
                <InlineMeta items={[
                  { label: "开场钩子", value: episode.opening_hook },
                  { label: "冲突", value: episode.conflict },
                  { label: "反转", value: episode.twist },
                  { label: "悬念", value: episode.cliffhanger },
                ]} />
              </DetailCard>
            ))}
          </div>
        </MarkdownSection>
      </div>
    );
  }

  if (characters.length || scenes.length || props.length) {
    return (
      <div className="space-y-4">
        <StyleAnchors output={output} />
        {!!characters.length && (
          <MarkdownSection title="人物">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
              {characters.slice(0, 12).map((character, index) => {
                const roleStyle = roleVisualStyle(character.role);
                return (
                  <DetailCard
                    key={index}
                    title={character.name || `人物 ${index + 1}`}
                    subtitle={character.role || "配角"}
                    cardStyle={roleStyle.card}
                    badgeStyle={roleStyle.badge}
                  >
                    <MarkdownParagraph>{character.description}</MarkdownParagraph>
                    <VisualProfile profile={character.visual_profile} />
                    {character.image_prompt && <MarkdownParagraph muted>{`生成提示词：${character.image_prompt}`}</MarkdownParagraph>}
                  </DetailCard>
                );
              })}
            </div>
          </MarkdownSection>
        )}
        {!!scenes.length && (
          <MarkdownSection title="场景">
            <div className="space-y-2">
              {scenes.slice(0, 10).map((scene, index) => (
                <DetailCard key={index} title={scene.name || `场景 ${index + 1}`}>
                  <MarkdownParagraph>{scene.description}</MarkdownParagraph>
                  {scene.image_prompt && <MarkdownParagraph muted>{`生成提示词：${scene.image_prompt}`}</MarkdownParagraph>}
                </DetailCard>
              ))}
            </div>
          </MarkdownSection>
        )}
        {!!props.length && (
          <MarkdownSection title="道具">
            <div className="space-y-2">
              {props.slice(0, 10).map((prop, index) => (
                <DetailCard key={index} title={prop.name || `道具 ${index + 1}`}>
                  <MarkdownParagraph>{prop.description}</MarkdownParagraph>
                  {prop.image_prompt && <MarkdownParagraph muted>{`生成提示词：${prop.image_prompt}`}</MarkdownParagraph>}
                </DetailCard>
              ))}
            </div>
          </MarkdownSection>
        )}
      </div>
    );
  }

  return <GenericMarkdown value={output} />;
};

const SmallButton = ({ children, onClick, disabled, active, icon: Icon, title }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="h-8 px-2.5 rounded-md text-sm flex items-center gap-1.5 border transition-colors disabled:opacity-50 cursor-pointer"
      style={{
        borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
        backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panelSoft,
        color: active ? theme.colors.accentPrimary : "var(--color-text-secondary)",
      }}
    >
      {Icon && <Icon size={13} className={Icon === Loader2 ? "animate-spin" : ""} />}
      {children}
    </button>
  );
};

const PanelTitle = ({ icon: Icon, title, action }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <div className="h-9 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon size={14} style={{ color: theme.colors.accentPrimary }} />}
        <span className="text-base font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</span>
      </div>
      {action}
    </div>
  );
};

const CandidateCard = ({ candidate, active, selected, loading, onSelect }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const status = meta(candidate, "provider_status");
  const modelWebSearch = status === "model_web_search";
  const aiGenerated = status === "ai_generated";
  const liveSearch = status === "live_search" || modelWebSearch;
  const aiRanked = ["llm_planned_and_ranked", "model_native_web_search", "ai_theme_ideation"].includes(meta(candidate, "selection_mode"));
  const storyLine = candidateStoryLine(candidate);
  return (
    <button
      type="button"
      onClick={() => onSelect(candidate)}
      disabled={loading}
      className="agent-motion-card w-full text-left rounded-md border p-2.5 space-y-2 transition-colors cursor-pointer disabled:opacity-60"
      style={{
        borderColor: selected ? theme.colors.accentPrimary : active ? "var(--color-success)" : "var(--color-border-primary)",
        backgroundColor: selected ? "var(--color-accent-light)" : active ? "var(--color-green-light)" : SURFACE.panelSoft,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0"
            style={{
              borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
              backgroundColor: selected ? theme.colors.accentPrimary : SURFACE.input,
              color: "white",
            }}
          >
            {selected && <Check size={11} />}
          </span>
          <div className="text-base font-semibold leading-6 line-clamp-2" style={{ color: "var(--color-text-primary)" }}>
            {candidate.title}
          </div>
        </div>
        {loading ? <Loader2 size={13} className="animate-spin shrink-0" /> : <ArrowUpRight size={13} className="shrink-0" style={{ color: theme.colors.accentPrimary }} />}
      </div>
      <div className="text-sm leading-6 line-clamp-3" style={{ color: "var(--color-text-muted)" }}>{candidate.summary}</div>
      {storyLine && (
        <div className="rounded-md border px-3 py-2 space-y-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div className="text-sm font-semibold" style={{ color: "var(--color-accent-primary)" }}>一句话故事</div>
          <div className="text-sm leading-6 line-clamp-2" style={{ color: "var(--color-text-primary)" }}>{storyLine}</div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="px-1.5 py-0.5 rounded text-sm" style={TONE_STYLE.info}>
          热度 {score(meta(candidate, "heat_score"))}
        </span>
        <span className="px-1.5 py-0.5 rounded text-sm" style={TONE_STYLE.accent}>
          奇葩 {score(meta(candidate, "oddity_score"))}
        </span>
        <span className="px-1.5 py-0.5 rounded text-sm" style={liveSearch ? TONE_STYLE.success : aiGenerated ? TONE_STYLE.purple : TONE_STYLE.muted}>
          {modelWebSearch ? "全网线索" : liveSearch ? "实时" : aiGenerated ? "AI发散" : "候选"}
        </span>
        {aiRanked && (
          <span className="px-1.5 py-0.5 rounded text-sm" style={TONE_STYLE.purple}>
            推荐筛选
          </span>
        )}
        {active && (
          <span className="px-1.5 py-0.5 rounded text-sm" style={TONE_STYLE.success}>
            已提交
          </span>
        )}
      </div>
      <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
        {meta(candidate, "source", "未知来源")} · {candidate.publishedAt || "未知时间"}
      </div>
    </button>
  );
};

const OptionCards = ({
  options,
  selectedOptionId,
  onSelect,
  disabled = false,
  onGenerateImage,
  onGenerateAllImages,
  generatingOptionId = "",
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [previewImage, setPreviewImage] = useState(null);
  if (!options.length) return null;
  const pendingImageOptions = options.filter((option) => !firstText(option.image_url, option.selected_image_url) && option.image_prompt);
  const imageGenerating = Boolean(generatingOptionId);
  return (
    <>
      {onGenerateAllImages && pendingImageOptions.length > 1 && (
        <div
          className="mt-3 rounded-md border px-3 py-2 flex flex-wrap items-center justify-between gap-2"
          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}
        >
          <div className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            还有 {pendingImageOptions.length} 张概念图待生成，建议按顺序生成，避免互相覆盖。
          </div>
          <button
            type="button"
            onClick={onGenerateAllImages}
            disabled={imageGenerating}
            className="h-8 px-3 rounded-md border inline-flex items-center gap-1.5 text-sm font-semibold cursor-pointer disabled:opacity-60"
            style={{
              borderColor: theme.colors.accentPrimary,
              backgroundColor: "var(--color-accent-light)",
              color: theme.colors.accentPrimary,
            }}
          >
            {imageGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            生成全部概念图
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2 mt-3">
        {options.map((option, index) => {
          const optionImageUrl = firstText(option.image_url, option.selected_image_url);
          const title = option.title || `方案 ${index + 1}`;
          const selected = String(selectedOptionId) === String(option.id);
          const currentImageGenerating = String(generatingOptionId) === String(option.id);
          const selectOption = () => {
            if (!disabled) onSelect(option.id);
          };
          return (
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={selected}
              key={option.id}
              onClick={selectOption}
              onKeyDown={(event) => {
                if (disabled) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(option.id);
                }
              }}
              className="agent-motion-card text-left rounded-md border p-3 space-y-2 transition-colors cursor-pointer"
              style={{
                borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
                backgroundColor: selected ? "var(--color-accent-light)" : SURFACE.panelSoft,
                opacity: disabled && !selected ? 0.74 : 1,
              }}
            >
              {optionImageUrl && (
                <StyleImageThumb imageUrl={optionImageUrl} title={title} onPreview={setPreviewImage} />
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <span
                    className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0"
                    style={{
                      borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: selected ? theme.colors.accentPrimary : SURFACE.input,
                      color: "white",
                    }}
                  >
                    {selected && <Check size={11} />}
                  </span>
                  <div className="text-base font-semibold leading-6" style={{ color: "var(--color-text-primary)" }}>{title}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-sm px-1.5 py-0.5 rounded" style={{ color: "var(--color-text-muted)", backgroundColor: SURFACE.raised }}>单选</span>
                  {option.recommended && (
                    <span className="text-sm px-1.5 py-0.5 rounded" style={{ color: theme.colors.accentPrimary, backgroundColor: SURFACE.raised }}>推荐</span>
                  )}
                </div>
              </div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>{option.summary}</div>
              {option.image_generation_error && (
                <div className="text-sm leading-5" style={{ color: "var(--color-warning)" }}>
                  参考图暂未生成：{option.image_generation_error}
                </div>
              )}
              {!optionImageUrl && option.image_prompt && onGenerateImage && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onGenerateImage(option.id);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  disabled={imageGenerating}
                  className="h-8 px-3 rounded-md border inline-flex items-center gap-1.5 text-sm font-semibold cursor-pointer disabled:opacity-60"
                  style={{
                    borderColor: theme.colors.accentPrimary,
                    backgroundColor: "var(--color-accent-light)",
                    color: theme.colors.accentPrimary,
                  }}
                >
                  {currentImageGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {currentImageGenerating ? "生成中" : option.image_generation_status === "failed" ? "重新生成概念图" : "生成概念图"}
                </button>
              )}
              {option.image_prompt && (
                <div className="text-sm leading-5 line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                  参考图提示词：{option.image_prompt}
                </div>
              )}
              {!!(option.pros || []).length && (
                <div className="text-sm line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                  {(option.pros || []).slice(0, 2).join(" / ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <StyleImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />
    </>
  );
};

const ChatMessage = ({ role, title, subtitle, children }) => {
  const isUser = role === "user";
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <div className={`agent-motion-message flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={TONE_STYLE.info}>
          <Bot size={16} />
        </div>
      )}
      <div className={`max-w-[860px] min-w-0 rounded-md border px-4 py-3 space-y-2 ${isUser ? "order-1" : ""}`}
        style={{
          borderColor: isUser ? `${theme.colors.accentPrimary}55` : "var(--color-border-primary)",
          backgroundColor: isUser ? "var(--color-accent-light)" : SURFACE.panelSoft,
        }}>
        {(title || subtitle) && (
          <div className="flex items-center justify-between gap-3">
            {title && <div className="text-base font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</div>}
            {subtitle && <div className="text-sm shrink-0" style={{ color: "var(--color-text-muted)" }}>{subtitle}</div>}
          </div>
        )}
        <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>{children}</div>
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={TONE_STYLE.accent}>
          <MessageSquareText size={16} />
        </div>
      )}
    </div>
  );
};

const ReadableValue = ({ value, depth = 0 }) => {
  if (value == null || value === "") return null;

  if (isPrimitive(value)) {
    return <span>{displayPrimitive(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (!value.length) return null;
    const primitiveOnly = value.every(isPrimitive);
    if (primitiveOnly) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.slice(0, 12).map((item, index) => (
            <span key={`${item}-${index}`} className="px-1.5 py-0.5 rounded text-sm" style={{ backgroundColor: SURFACE.raised, color: "var(--color-text-secondary)" }}>
              {displayPrimitive(item)}
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {value.slice(0, 6).map((item, index) => (
          <div key={index} className="rounded-md border p-2.5 space-y-1.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
            {!isPrimitive(item) && (
              <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {valueTitle(item, `条目 ${index + 1}`)}
              </div>
            )}
            <ReadableValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(value)
    .filter(([key, item]) => item != null && item !== "" && key !== "options" && key !== "id")
    .slice(0, depth > 1 ? 8 : 14);

  if (!entries.length) return null;

  return (
    <div className="space-y-2">
      {entries.map(([key, item]) => (
        <div key={key} className={depth === 0 ? "space-y-1" : "grid grid-cols-[88px_minmax(0,1fr)] gap-2"}>
          <div className="text-sm font-semibold" style={{ color: "var(--color-text-muted)" }}>
            {humanLabel(key)}
          </div>
          <div className="text-sm leading-6 min-w-0 break-words" style={{ color: "var(--color-text-secondary)" }}>
            <ReadableValue value={item} depth={depth + 1} />
          </div>
        </div>
      ))}
    </div>
  );
};

const VisualOutput = ({ output }) => {
  if (!output) return null;
  if (output.error) {
    return <div style={{ color: "var(--color-error)" }}>{output.error}</div>;
  }
  const title = output.title || output.logline || output.premise;
  return (
    <div className="rounded-md border p-4 space-y-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
      {title && (
        <div className="text-lg font-semibold leading-7" style={{ color: "var(--color-text-primary)" }}>
          {title}
        </div>
      )}
      <MarkdownOutput output={output} />
    </div>
  );
};

const StageHandoffStep = ({ number, title, detail, state = "pending", icon: Icon }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const active = state === "active";
  const done = state === "done";
  const color = done ? "var(--color-success)" : active ? theme.colors.accentPrimary : "var(--color-text-muted)";
  const background = done ? "var(--color-green-light)" : active ? "var(--color-accent-light)" : SURFACE.raised;
  return (
    <div
      className="rounded-md border p-3 flex items-start gap-3 min-w-0"
      style={{
        borderColor: active ? theme.colors.accentPrimary : done ? "var(--color-success)" : "var(--color-border-primary)",
        backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panelSoft,
      }}
    >
      <span
        className="w-7 h-7 rounded-md flex items-center justify-center text-sm font-semibold shrink-0"
        style={{ backgroundColor: background, color }}
      >
        {Icon ? <Icon size={14} className={active && Icon === Loader2 ? "animate-spin" : ""} /> : number}
      </span>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-primary)" }}>{title}</div>
        <div className="text-sm leading-5 mt-0.5" style={{ color: "var(--color-text-muted)" }}>{detail}</div>
      </div>
    </div>
  );
};

const StageHandoffPanel = ({ stage, guide, previousStage, confirming = false }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const title = guide?.title || stage?.name || "下一阶段";
  const previousTitle = previousStage?.name || "上一阶段";
  return (
    <section
      className="max-w-[900px] mx-auto rounded-md border overflow-hidden"
      style={{ borderColor: confirming ? theme.colors.accentPrimary : "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}
      aria-label={`${title} 阶段交接`}
    >
      <div className="h-1.5" style={{ backgroundColor: confirming ? theme.colors.accentPrimary : "var(--color-info)" }} />
      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={confirming ? TONE_STYLE.accent : TONE_STYLE.info}>
              {confirming ? <Loader2 size={18} className="animate-spin" /> : <Compass size={18} />}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: confirming ? theme.colors.accentPrimary : "var(--color-info)" }}>
                {confirming ? "正在确认并切换阶段" : "下一阶段已准备好"}
              </div>
              <div className="text-xl font-semibold leading-7 truncate" style={{ color: "var(--color-text-primary)" }}>{title}</div>
            </div>
          </div>
          <span className="text-sm px-2 py-1 rounded-md shrink-0" style={confirming ? TONE_STYLE.accent : TONE_STYLE.info}>
            {confirming ? "请稍等" : "等待你的输入"}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <StageHandoffStep
            number={1}
            title="已确认上一步"
            detail={confirming ? `正在归档「${previousTitle}」` : previousTitle}
            state="done"
            icon={Check}
          />
          <StageHandoffStep
            number={2}
            title={confirming ? "正在切换阶段" : "切换完成"}
            detail={confirming ? "保存选择和物料版本" : "流程已停在下一阶段"}
            state={confirming ? "active" : "done"}
            icon={confirming ? Loader2 : Check}
          />
          <StageHandoffStep
            number={3}
            title={confirming ? "准备下一步" : "等待你输入"}
            detail={confirming ? `即将进入「${title}」` : "发送后导演才开始处理"}
            state={confirming ? "pending" : "active"}
            icon={confirming ? null : Send}
          />
        </div>

        <div className="text-sm leading-7" style={{ color: "var(--color-text-secondary)" }}>
          {confirming ? "正在把当前确认内容沉淀到左侧物料栏，并准备下一阶段的输入入口。" : guide?.goal}
        </div>
        <div className="rounded-md border px-3 py-2 flex items-start gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}>
          <Target size={14} className="mt-1 shrink-0" style={{ color: theme.colors.accentPrimary }} />
          <div className="text-sm leading-7" style={{ color: "var(--color-text-primary)" }}>
            {confirming ? `下一步会进入「${title}」，请稍后在底部输入你的生成要求。` : guide?.prompt}
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
          <Send size={13} className="mt-1 shrink-0" />
          <span>{confirming ? "现在还不能继续输入，等切换完成后再发给导演。" : "导演还没有开始处理。请在底部输入要求并点击“发送给导演”，才会生成当前阶段内容。"}</span>
        </div>
      </div>
    </section>
  );
};

const StageConfirmModal = ({
  open,
  currentStage,
  nextStage,
  guide,
  selectedLabel,
  selectedFromHistory,
  loading,
  onCancel,
  onConfirm,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const nextTitle = nextStage?.name || guide?.title || "下一阶段";
  const currentTitle = currentStage?.name || "当前阶段";

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, loading, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center p-5 animate-in fade-in duration-150"
      style={{ backgroundColor: "var(--overlay-medium)" }}
      role="dialog"
      aria-modal="true"
      aria-label="确认进入下一步"
      onMouseDown={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="agent-motion-card w-full max-w-[560px] rounded-md border overflow-hidden"
        style={{ borderColor: theme.colors.accentPrimary, backgroundColor: SURFACE.panel }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="h-1.5" style={{ backgroundColor: theme.colors.accentPrimary }} />
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={TONE_STYLE.accent}>
                <Compass size={18} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold" style={{ color: theme.colors.accentPrimary }}>准备进入下一步</div>
                <div className="text-xl font-semibold leading-7" style={{ color: "var(--color-text-primary)" }}>{nextTitle}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer disabled:opacity-50"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
              aria-label="关闭"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <StageHandoffStep number={1} title="已选择方案" detail={selectedLabel || "当前选项"} state="done" icon={Check} />
            <StageHandoffStep number={2} title="确认归档" detail={currentTitle} state="active" icon={CheckCircle2} />
            <StageHandoffStep number={3} title="进入下一步" detail={nextTitle} state="pending" icon={Send} />
          </div>

          <div className="rounded-md border px-3 py-2 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-primary)" }}>
              你已经选定「{selectedFromHistory ? "历史风格图：" : ""}{selectedLabel || "当前方案"}」。确认后会把当前阶段内容固定到左侧物料栏，并切换到「{nextTitle}」。
            </div>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
              如果还想让导演继续调整，请取消后在底部输入修改意见再发送。
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="h-9 px-4 rounded-md text-sm font-semibold border cursor-pointer disabled:opacity-50"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
            >
              先不进入
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="h-9 px-4 rounded-md text-sm font-semibold border flex items-center gap-2 cursor-pointer disabled:opacity-50"
              style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {loading ? "正在确认" : "确认进入下一步"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StageErrorGatePanel = ({ stage, output, onBack }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <section
      className="max-w-[900px] mx-auto rounded-md border overflow-hidden"
      style={{ borderColor: "var(--color-error)", backgroundColor: SURFACE.panelSoft }}
      aria-label={`${stage?.name || "上一阶段"} 生成失败`}
    >
      <div className="h-1.5" style={{ backgroundColor: "var(--color-error)" }} />
      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(220,38,38,0.14)", color: "var(--color-error)" }}>
              <RefreshCw size={18} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "var(--color-error)" }}>上一阶段没有成功完成</div>
              <div className="text-xl font-semibold leading-7 truncate" style={{ color: "var(--color-text-primary)" }}>{stage?.name || "出错阶段"}</div>
            </div>
          </div>
          <span className="text-sm px-2 py-1 rounded-md shrink-0" style={{ backgroundColor: "rgba(220,38,38,0.14)", color: "var(--color-error)" }}>
            需要重试
          </span>
        </div>
        <div className="rounded-md border px-3 py-2 text-sm leading-6" style={{ borderColor: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.1)", color: "var(--color-text-secondary)" }}>
          {output?.error || "上一阶段返回了错误结果，不能继续进入下一步。"}
        </div>
        <div className="text-sm leading-7" style={{ color: "var(--color-text-muted)" }}>
          请回到出错阶段重新生成。生成成功后，才可以确认并进入下一步。
        </div>
        <button
          type="button"
          onClick={onBack}
          className="h-9 px-4 rounded-md text-sm font-semibold flex items-center gap-2 border cursor-pointer"
          style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
        >
          <RefreshCw size={14} />
          回到出错阶段
        </button>
      </div>
    </section>
  );
};

const ArtifactList = ({ artifacts, stages, onSelectStage }) => {
  if (!artifacts?.length) {
    return (
      <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panelSoft }}>
        确认素材和阶段后，这里会沉淀素材理解、改编方案、短剧设定、风格锚点和分集大纲。
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {artifacts.slice(-8).reverse().map((artifact) => {
        const content = parseMaybeJson(artifact.contentJson, {});
        const stageName = stages.find((item) => item.key === artifact.stageKey)?.name || "素材";
        return (
          <button
            key={artifact.id}
            type="button"
            onClick={() => artifact.stageKey && onSelectStage(artifact.stageKey)}
            className="w-full rounded-md border p-2.5 text-left space-y-1 cursor-pointer"
            style={{
              borderColor: artifact.selected ? "var(--color-success)" : "var(--color-border-primary)",
              backgroundColor: artifact.selected ? "var(--color-green-light)" : SURFACE.panelSoft,
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                {valueTitle(content, artifact.artifactType || "物料")}
              </div>
              {artifact.selected && <Check size={12} style={{ color: "var(--color-success)" }} />}
            </div>
            <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
              {stageName} · 版本 {artifact.version || 1}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const LaunchProgressPanel = ({ stages, currentKey, stageRuns, productionReady, onSelectStage, onOpenProduction }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const stageNameByKey = new Map((stages || []).map((item) => [item.key, item.name]));
  return (
    <section className="rounded-md border p-3 space-y-3" style={{ borderColor: productionReady ? "var(--color-success)" : "var(--color-border-primary)", backgroundColor: productionReady ? "var(--color-green-light)" : SURFACE.panelSoft }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>立项进度</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>
            完成分集大纲后，才进入按集生产
          </div>
        </div>
        {productionReady && onOpenProduction && (
          <button
            type="button"
            onClick={onOpenProduction}
            className="h-8 px-2.5 rounded-md border text-sm font-semibold flex items-center gap-1.5 cursor-pointer shrink-0"
            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            <Workflow size={13} />
            去生产
          </button>
        )}
      </div>
      <div className="space-y-2">
        {LAUNCH_STEPS.map((item, index) => {
          const run = getRunOutput(stageRuns, item.key);
          const output = parseMaybeJson(run?.outputJson, null);
          const failed = runHasError(run, output);
          const done = run?.status === "CONFIRMED" && !failed;
          const active = item.key === currentKey;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelectStage(item.key)}
              className="w-full rounded-md border p-2.5 text-left flex gap-2 cursor-pointer"
              style={{
                borderColor: failed ? "var(--color-error)" : done ? "var(--color-success)" : active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panel,
              }}
            >
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-sm shrink-0"
                style={{
                  backgroundColor: failed ? "rgba(220,38,38,0.14)" : done ? "var(--color-green-light)" : SURFACE.raised,
                  color: failed ? "var(--color-error)" : done ? "var(--color-success)" : active ? theme.colors.accentPrimary : "var(--color-text-muted)",
                }}
              >
                {done ? <Check size={12} /> : failed ? "!" : index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-primary)" }}>
                  {stageNameByKey.get(item.key) || item.title}
                </div>
                <div className="text-xs leading-5 mt-0.5" style={{ color: "var(--color-text-muted)" }}>{item.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const LaunchCompletePanel = ({ onOpenProduction }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <section className="agent-motion-card rounded-md border overflow-hidden" style={{ borderColor: "var(--color-success)", backgroundColor: "var(--color-green-light)" }}>
      <div className="h-1.5" style={{ backgroundColor: "var(--color-success)" }} />
      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-green-light)", color: "var(--color-success)" }}>
              <CheckCircle2 size={20} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: "var(--color-success)" }}>项目已立项</div>
              <div className="text-xl font-bold leading-7" style={{ color: "var(--color-text-primary)" }}>现在进入按集生产</div>
              <div className="text-sm leading-6 mt-1" style={{ color: "var(--color-text-secondary)" }}>
                后续不再要求一次性写完所有集。你可以写一集、改一集、拆一集分镜，再继续补新集。
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenProduction}
            className="h-12 px-5 rounded-md border text-base font-bold flex items-center gap-2 cursor-pointer shrink-0"
            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            <Workflow size={15} />
            进入分集生产台
          </button>
        </div>
      </div>
    </section>
  );
};

const FlowStepper = ({ activeStep, steps }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const stepItems = steps || WORKBENCH_MODES.adaptation.flowSteps;
  return (
    <div className="h-9 max-w-full overflow-x-auto custom-scrollbar rounded-full border px-3 flex items-center gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
      {stepItems.map((item) => {
        const done = item.id < activeStep;
        const active = item.id === activeStep;
        return (
          <div key={item.id} className="flex items-center gap-2 shrink-0">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{
                backgroundColor: done ? "var(--color-green-light)" : active ? theme.colors.accentPrimary : SURFACE.raised,
                color: done ? "var(--color-success)" : active ? "white" : "var(--color-text-muted)",
              }}
            >
              {done ? <Check size={13} /> : item.id}
            </span>
            <span
              className="text-sm font-semibold"
              style={{ color: active ? theme.colors.accentPrimary : done ? "var(--color-success)" : "var(--color-text-muted)" }}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const FlowShell = ({ activeStep, session, children, onCreateSession, creating, mockAgentEnabled, onToggleMockAgent, config, hideFlowControls = false }) => (
  <div className="w-full h-full overflow-hidden flex flex-col" style={{ backgroundColor: SURFACE.page }}>
    <header className="h-16 shrink-0 border-b px-4 flex items-center gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
      <div className="min-w-[240px] flex items-center gap-3">
        <span className="w-9 h-9 rounded-md flex items-center justify-center" style={TONE_STYLE.accent}>
          <Wand2 size={18} />
        </span>
        <div className="min-w-0">
          <div className="text-base font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{config?.title || WORKBENCH_MODES.adaptation.title}</div>
          <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
            {session ? `草稿 ${session.id} · 项目立项` : (config?.subtitle || WORKBENCH_MODES.adaptation.subtitle)}
          </div>
        </div>
      </div>
      {!hideFlowControls && (
        <>
          <div className="flex-1 flex justify-center min-w-0">
            <FlowStepper activeStep={activeStep} steps={config?.flowSteps} />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SmallButton onClick={onToggleMockAgent} icon={Bot} active={mockAgentEnabled} title="使用示例数据快速体验">
              {mockAgentEnabled ? "演示中" : "演示模式"}
            </SmallButton>
            <SmallButton onClick={onCreateSession} disabled={creating} icon={creating ? Loader2 : RefreshCw}>
              新建草稿
            </SmallButton>
          </div>
        </>
      )}
      {hideFlowControls && <div className="flex-1" />}
    </header>
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-6">
      {children}
    </div>
  </div>
);

const ONE_CLICK_TEXT_MODEL_OPTIONS = TEXT_AI_PROVIDERS.map(toGlobalProviderOption);
const ONE_CLICK_IMAGE_MODEL_OPTIONS = IMAGE_AI_PROVIDERS.map(toGlobalProviderOption);

const normalizeOneClickTextProvider = normalizeTextProvider;
const normalizeOneClickImageProvider = normalizeImageProvider;

const SourceGuidePage = ({
  projectId,
  session,
  sessions,
  sourceType,
  sourceQuery,
  sourceText,
  loading,
  onCreateSession,
  onRefreshSession,
  onSearch,
  onSourceTypeChange,
  onSourceQueryChange,
  onSourceTextChange,
  mockAgentEnabled,
  onToggleMockAgent,
  config,
  autoMode,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { setCurrentStyle } = useStyle();
  const sourceTypes = config?.sourceTypes || SOURCE_TYPES;
  const activeSource = sourceTypes.find((item) => item.id === sourceType) || sourceTypes[0];
  const searchDisabled = loading === "search" || loading === "create" || (isTextSourceType(sourceType) ? !sourceText.trim() : !sourceQuery.trim());
  const isAutoExplainerEntry = autoMode === "explainer";
  const isDialogueAutoEntry = autoMode === "dialogue";
  const isAutoWorkflowEntry = isAutoExplainerEntry || isDialogueAutoEntry;
  const autoWorkflowStateKey = isDialogueAutoEntry ? DIALOGUE_AUTO_STATE_KEY : ONE_CLICK_EXPLAINER_STATE_KEY;
  const [autoWorkflowLoading, setAutoWorkflowLoading] = useState(false);
  const [autoWorkflowStatus, setAutoWorkflowStatus] = useState(null);
  const [autoWorkflowResult, setAutoWorkflowResult] = useState(null);
  const [autoWorkflowStyle, setAutoWorkflowStyleState] = useState(() => {
    const saved = window.localStorage.getItem(`${autoWorkflowStateKey}_style`) || window.localStorage.getItem("one_click_explainer_style") || window.localStorage.getItem("gen_style");
    return normalizeVisualStyleKey(saved, "modern_cdrama");
  });
  const [autoWorkflowTextProvider, setAutoWorkflowTextProviderState] = useState(() => {
    const modeSaved = window.localStorage.getItem(`${autoWorkflowStateKey}_text_provider`);
    const legacySaved = isDialogueAutoEntry ? null : window.localStorage.getItem("one_click_explainer_text_provider");
    return normalizeOneClickTextProvider(modeSaved || legacySaved);
  });
  const [autoWorkflowImageProvider, setAutoWorkflowImageProviderState] = useState(() => {
    const modeSaved = window.localStorage.getItem(`${autoWorkflowStateKey}_image_provider`);
    const legacySaved = isDialogueAutoEntry ? null : window.localStorage.getItem("one_click_explainer_image_provider");
    return normalizeOneClickImageProvider(modeSaved || legacySaved);
  });
  useEffect(() => {
    const savedTextProvider = normalizeOneClickTextProvider(window.localStorage.getItem(`${autoWorkflowStateKey}_text_provider`)
      || (isDialogueAutoEntry ? null : window.localStorage.getItem("one_click_explainer_text_provider"))
      || "wangsugem");
    const savedImageProvider = normalizeOneClickImageProvider(window.localStorage.getItem(`${autoWorkflowStateKey}_image_provider`)
      || (isDialogueAutoEntry ? null : window.localStorage.getItem("one_click_explainer_image_provider"))
      || "wangsugem");
    setAutoWorkflowTextProviderState(savedTextProvider);
    setAutoWorkflowImageProviderState(savedImageProvider);
  }, [autoWorkflowStateKey, isDialogueAutoEntry]);
  const [autoWorkflowFullAuto, setAutoWorkflowFullAutoState] = useState(false);
  const [autoWorkflowDownloading, setAutoWorkflowDownloading] = useState(false);
  const autoWorkflowAbortRef = useRef(null);
  const autoWorkflowPollStopRef = useRef(null);
  const autoWorkflowTaskRef = useRef(null);
  const autoWorkflowContinueInFlightRef = useRef(false);
  const autoWorkflowStorageKey = useMemo(
    () => projectId ? `${autoWorkflowStateKey}_state_v1:${projectId}` : null,
    [autoWorkflowStateKey, projectId]
  );
  const autoWorkflowBackendStateUrl = useMemo(
    () => projectId ? `${API_HOST}/api/adaptation-agent/projects/${projectId}/workspace-state/${autoWorkflowStateKey}` : null,
    [autoWorkflowStateKey, projectId]
  );
  const autoWorkflowFullAutoKey = useMemo(
    () => projectId ? `${autoWorkflowStateKey}_full_auto:${projectId}` : `${autoWorkflowStateKey}_full_auto:global`,
    [autoWorkflowStateKey, projectId]
  );
  const autoWorkflowTraceEnabled = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      return params.get("trace") === "1"
        || params.get("workflowTrace") === "1"
        || window.localStorage.getItem("auto_workflow_trace") === "1";
    } catch {
      return false;
    }
  }, []);
  const canRunAutoWorkflow = sourceType === "novel_text" && Boolean(projectId) && !autoWorkflowLoading;
  const canRunExistingEpisodeWorkflow = Boolean(projectId) && !autoWorkflowLoading;
  const autoWorkflowVideoProvider = getProjectVideoSubmissionProvider(projectId);
  const autoWorkflowSeedance25Capabilities = getSeedance25Capabilities(autoWorkflowVideoProvider);
  const autoWorkflowVideoModel = autoWorkflowSeedance25Capabilities?.model || getProjectDefaultVideoModel(projectId, true);
  const autoWorkflowProjectRatio = getProjectDefaultVideoRatio(projectId);
  const autoWorkflowVideoRatio = autoWorkflowSeedance25Capabilities
    ? normalizeSeedance25Ratio(autoWorkflowVideoProvider, autoWorkflowProjectRatio)
    : autoWorkflowProjectRatio;
  const autoWorkflowVideoResolution = autoWorkflowSeedance25Capabilities
    ? normalizeSeedance25Resolution(autoWorkflowVideoProvider)
    : normalizeVideoResolutionForProvider(
        autoWorkflowVideoProvider,
        "480p",
        "480p",
      );
  const autoWorkflowSegmentSeconds = isSd25Project(projectId) ? 30 : 15;
  const selectedAutoStyle = STYLE_OPTIONS.find((item) => item.value === autoWorkflowStyle) || STYLE_OPTIONS.find((item) => item.value === "modern_cdrama") || STYLE_OPTIONS[0];
  const autoWorkflowStopped = autoWorkflowStatus?.stage === "stopped"
    || autoWorkflowStatus?.status === "stopped"
    || autoWorkflowResult?.status === "stopped"
    || autoWorkflowResult?.stage === "stopped";
  const autoWorkflowNeedsConfirmation = !autoWorkflowStopped && Boolean(autoWorkflowResult?.requiresAssetConfirmation);
  const autoWorkflowEpisodeIds = Array.isArray(autoWorkflowResult?.episodes)
    ? autoWorkflowResult.episodes.map((episode) => episode?.episodeId).filter(Boolean)
    : [];
  const autoWorkflowEpisodes = useMemo(() => {
    const rows = asArray(autoWorkflowResult?.episodes).length
      ? asArray(autoWorkflowResult?.episodes)
      : asArray(autoWorkflowResult?.episodeResults);
    return rows.filter((episode) => episode?.episodeId || episode?.id);
  }, [autoWorkflowResult]);
  const autoWorkflowAssetSummary = autoWorkflowResult?.assetSummary || {};
  const autoWorkflowVideoCounts = useMemo(() => countAutoWorkflowVideos(autoWorkflowEpisodes), [autoWorkflowEpisodes]);
  const autoWorkflowEpisodeCount = Number(autoWorkflowResult?.episodeCount || autoWorkflowEpisodes.length || 0);
  const autoWorkflowStage = firstText(autoWorkflowStatus?.stage, autoWorkflowStatus?.currentStage, autoWorkflowResult?.stage, autoWorkflowResult?.status);
  const autoWorkflowStageText = firstText(autoWorkflowStatus?.stageText, autoWorkflowStatus?.currentStageText, autoWorkflowResult?.stageText, "等待开始");
  const autoWorkflowIsDone = autoWorkflowStage === "done" || autoWorkflowStatus?.status === "done" || autoWorkflowResult?.status === "done";
  const autoWorkflowRecoveredFromProject = Boolean(autoWorkflowResult?.recoveredFromProject || autoWorkflowResult?.completedFromProject);
  const canContinueAutoWorkflow = Boolean(projectId) && autoWorkflowNeedsConfirmation && autoWorkflowEpisodeIds.length > 0 && !autoWorkflowLoading;
  const canResumeAutoWorkflow = Boolean(projectId) && autoWorkflowStopped && !autoWorkflowLoading;
  const autoWorkflowCopy = isDialogueAutoEntry ? {
    title: "对话短剧自动流",
    description: "有剧本时复用剧本中心；无剧本时可直接粘贴小说，先改成对话短剧并导入剧本中心，再生成资产和视频。",
    progressHint: "状态已保存到项目，关闭页面后会从后端状态恢复。",
    firstStageTitle: "剧本确认",
    firstStageActive: sourceText.trim() ? "小说已输入，待改写剧本" : "等待剧本中心或小说输入",
    firstStageDone: `已检测到 ${autoWorkflowEpisodeCount} 集`,
    firstStageThirdLine: "无剧本时会按对话短剧规范改写并导入",
    startButton: sourceText.trim() ? "小说改剧本并生成资产" : "检查剧本并生成资产",
    startWithTextButton: "按现有剧本生成资产",
    sourcePlaceholder: "如果剧本中心还没有剧本，可在这里粘贴小说原文。系统会先按对话短剧规范改写成分集剧本并导入剧本中心；已有分集剧本时可留空。",
    assetDoneText: "资产已生成，请确认后继续",
    taskErrorText: "对话短剧自动流生成失败",
  } : {
    title: "一键解说剧",
    description: "导入小说后先生成角色、场景、道具；确认资产后自动入库、拆小故事板并按项目视频线路生成视频。",
    progressHint: "状态已保存到项目，关闭页面后会从后端状态恢复。",
    firstStageTitle: "导入与改写",
    firstStageActive: sourceText.trim() ? "小说已输入" : "等待粘贴小说",
    firstStageDone: `已生成 ${autoWorkflowEpisodeCount} 集`,
    firstStageThirdLine: autoWorkflowStage === "novel_to_script" ? "正在转旁白剧格式" : "旁白剧格式用于后续生成",
    startButton: "开始生成资产",
    startWithTextButton: "生成资产待确认",
    sourcePlaceholder: "粘贴小说正文、章节节选或完整梗概。",
    assetDoneText: "资产已生成，请确认后继续",
    taskErrorText: "一键解说剧生成失败",
  };
  const setAutoWorkflowStyle = (style) => {
    const normalized = normalizeVisualStyleKey(style, "modern_cdrama");
    setAutoWorkflowStyleState(normalized);
    window.localStorage.setItem(`${autoWorkflowStateKey}_style`, normalized);
    setCurrentStyle(normalized);
  };
  const setAutoWorkflowTextProvider = (provider) => {
    provider = normalizeOneClickTextProvider(provider);
    setAutoWorkflowTextProviderState(provider);
    window.localStorage.setItem(`${autoWorkflowStateKey}_text_provider`, provider);
  };
  const setAutoWorkflowImageProvider = (provider) => {
    provider = normalizeOneClickImageProvider(provider);
    setAutoWorkflowImageProviderState(provider);
    window.localStorage.setItem(`${autoWorkflowStateKey}_image_provider`, provider);
  };
  useEffect(() => {
    try {
      setAutoWorkflowFullAutoState(window.localStorage.getItem(autoWorkflowFullAutoKey) === "true");
    } catch {
      setAutoWorkflowFullAutoState(false);
    }
  }, [autoWorkflowFullAutoKey]);
  const saveAutoWorkflowState = useCallback((nextState = {}) => {
    if (!autoWorkflowStorageKey || !projectId) return;
    const snapshot = {
      projectId: Number(projectId),
      savedAt: Date.now(),
      style: nextState.style || autoWorkflowStyle,
      textProvider: nextState.textProvider || autoWorkflowTextProvider,
      imageProvider: nextState.imageProvider || autoWorkflowImageProvider,
      fullAuto: Object.prototype.hasOwnProperty.call(nextState, "fullAuto") ? Boolean(nextState.fullAuto) : autoWorkflowFullAuto,
      status: Object.prototype.hasOwnProperty.call(nextState, "status") ? nextState.status : autoWorkflowStatus,
      result: Object.prototype.hasOwnProperty.call(nextState, "result") ? nextState.result : autoWorkflowResult,
    };
    try {
      window.localStorage.setItem(autoWorkflowStorageKey, JSON.stringify(snapshot));
    } catch {
      // Ignore quota/private-mode failures; the workflow still works in memory.
    }
  }, [
    autoWorkflowImageProvider,
    autoWorkflowFullAuto,
    autoWorkflowResult,
    autoWorkflowStatus,
    autoWorkflowStorageKey,
    autoWorkflowStyle,
    autoWorkflowTextProvider,
    projectId,
  ]);
  const clearAutoWorkflowState = useCallback(() => {
    if (!autoWorkflowStorageKey) return;
    try {
      window.localStorage.removeItem(autoWorkflowStorageKey);
    } catch {
      // Ignore storage failures.
    }
  }, [autoWorkflowStorageKey]);

  const saveAutoWorkflowBackendSnapshot = useCallback((nextState = {}) => {
    if (!autoWorkflowBackendStateUrl || !projectId) return;
    const snapshot = {
      projectId: Number(projectId),
      stateKey: autoWorkflowStateKey,
      workflowKey: autoWorkflowStateKey,
      updatedAt: new Date().toISOString(),
      style: nextState.style || autoWorkflowStyle,
      textProvider: nextState.textProvider || autoWorkflowTextProvider,
      imageProvider: nextState.imageProvider || autoWorkflowImageProvider,
      fullAuto: Object.prototype.hasOwnProperty.call(nextState, "fullAuto") ? Boolean(nextState.fullAuto) : autoWorkflowFullAuto,
      status: nextState.status || autoWorkflowStatus?.status || autoWorkflowStatus?.stage || autoWorkflowResult?.status || "idle",
      stage: nextState.stage || autoWorkflowStatus?.stage || autoWorkflowResult?.stage,
      stageText: nextState.stageText || autoWorkflowStatus?.stageText,
      progress: Object.prototype.hasOwnProperty.call(nextState, "progress") ? nextState.progress : autoWorkflowStatus?.progress,
      taskId: nextState.taskId || autoWorkflowStatus?.taskId || autoWorkflowTaskRef.current,
      taskType: nextState.taskType,
      result: Object.prototype.hasOwnProperty.call(nextState, "result") ? nextState.result : autoWorkflowResult,
    };
    authenticatedFetch(autoWorkflowBackendStateUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(snapshot),
    }).catch((error) => {
      console.warn("[OneClickExplainer] 保存后端状态失败:", error);
    });
  }, [
    autoWorkflowBackendStateUrl,
    autoWorkflowImageProvider,
    autoWorkflowFullAuto,
    autoWorkflowResult,
    autoWorkflowStatus,
    autoWorkflowStyle,
    autoWorkflowTextProvider,
    autoWorkflowStateKey,
    projectId,
  ]);

  useEffect(() => {
    if (!autoWorkflowStorageKey || !projectId) return;
    let canceled = false;
    let stopPolling = null;

    const applySnapshot = (saved) => {
      if (!saved || Number(saved.projectId) !== Number(projectId)) return null;
      const savedStyle = saved.style || saved.config?.styleAnchor;
      if (savedStyle) {
        setAutoWorkflowStyleState(savedStyle);
        setCurrentStyle(savedStyle);
      }
      const savedTextProvider = saved.textProvider || saved.config?.textProvider;
      if (savedTextProvider) {
        setAutoWorkflowTextProviderState(savedTextProvider);
      }
      const savedImageProvider = saved.imageProvider || saved.config?.imageProvider;
      if (savedImageProvider) {
        setAutoWorkflowImageProviderState(savedImageProvider);
      }
      if (typeof saved.fullAuto === "boolean") {
        setAutoWorkflowFullAutoState(saved.fullAuto);
        try {
          window.localStorage.setItem(autoWorkflowFullAutoKey, saved.fullAuto ? "true" : "false");
        } catch {
          // Ignore storage failures.
        }
      }
      if (saved.taskId) {
        autoWorkflowTaskRef.current = saved.taskId;
      }

      const statusObject = saved.status && typeof saved.status === "object"
        ? saved.status
        : {
          stage: saved.stage,
          stageText: saved.stageText || (typeof saved.status === "string" ? saved.status : ""),
          progress: saved.progress,
        };
      if (statusObject?.stageText || statusObject?.stage || Number.isFinite(Number(statusObject?.progress))) {
        setAutoWorkflowStatus(statusObject);
      }

      const restoredResult = saved.result || (saved.requiresAssetConfirmation || Array.isArray(saved.episodes) || Array.isArray(saved.episodeResults)
        ? {
          ...saved,
          episodes: Array.isArray(saved.episodes) ? saved.episodes : saved.episodeResults,
        }
        : null);
      if (restoredResult) {
        setAutoWorkflowResult(restoredResult);
      }
      const recoveredProjectState = Boolean(
        saved.recoveredFromProject
          || saved.completedFromProject
          || restoredResult?.recoveredFromProject
          || restoredResult?.completedFromProject
      );
      if (recoveredProjectState) {
        setAutoWorkflowFullAutoState(false);
        try {
          window.localStorage.setItem(autoWorkflowFullAutoKey, "false");
        } catch {
          // Ignore storage failures.
        }
      }
      return { statusObject, restoredResult };
    };

    const restoreLocal = () => {
      try {
        const raw = window.localStorage.getItem(autoWorkflowStorageKey);
        if (!raw) return;
        applySnapshot(JSON.parse(raw));
      } catch {
        clearAutoWorkflowState();
      }
    };

    const resumePolling = (taskId) => {
      if (!taskId) return;
      autoWorkflowTaskRef.current = taskId;
      setAutoWorkflowLoading(true);
      autoWorkflowPollStopRef.current?.();
      stopPolling = startTaskPolling(taskId, {
        interval: 5000,
        onProgress: (status) => {
          if (canceled) return;
          const nextStatus = {
            stage: status?.currentStage || status?.stage,
            stageText: status?.currentStageText || status?.stageText || status?.status || "任务执行中",
            progress: status?.progress,
          };
          setAutoWorkflowStatus(nextStatus);
          saveAutoWorkflowState({ status: nextStatus });
        },
        onSuccess: (rawResult) => {
          if (canceled) return;
          const result = unwrapTaskResult(rawResult);
          const nextStage = result?.requiresAssetConfirmation ? "asset_review" : "done";
          const doneStatus = {
            stage: nextStage,
            stageText: result?.requiresAssetConfirmation ? "资产已生成，请确认后继续" : "任务已完成",
            progress: 100,
          };
          setAutoWorkflowResult(result);
          setAutoWorkflowStatus(doneStatus);
          setAutoWorkflowLoading(false);
          saveAutoWorkflowState({ result, status: doneStatus });
          saveAutoWorkflowBackendSnapshot({
            taskId,
            status: doneStatus.stage,
            stage: doneStatus.stage,
            stageText: doneStatus.stageText,
            progress: doneStatus.progress,
            result,
          });
          autoWorkflowPollStopRef.current = null;
          autoWorkflowTaskRef.current = null;
        },
        onError: (message) => {
          if (canceled) return;
          const failedStatus = { stage: "failed", stageText: message || "任务失败", progress: 100 };
          setAutoWorkflowStatus(failedStatus);
          setAutoWorkflowLoading(false);
          saveAutoWorkflowState({ status: failedStatus });
          autoWorkflowPollStopRef.current = null;
        },
      });
      autoWorkflowPollStopRef.current = stopPolling;
    };

    const load = async () => {
      let restoredBackend = false;
      if (autoWorkflowBackendStateUrl) {
        try {
          const backendState = await authenticatedFetch(autoWorkflowBackendStateUrl);
          if (!canceled && backendState && backendState.status && backendState.status !== "idle") {
            restoredBackend = true;
            applySnapshot(backendState);
            if (backendState.status === "processing" && backendState.taskId) {
              resumePolling(backendState.taskId);
            } else {
              setAutoWorkflowLoading(false);
            }
          }
        } catch (error) {
          console.warn("[OneClickExplainer] 读取后端状态失败，尝试 localStorage:", error);
        }
      }
      if (!canceled && !restoredBackend) {
        restoreLocal();
      }
    };

    load();
    return () => {
      canceled = true;
      stopPolling?.();
      if (autoWorkflowPollStopRef.current === stopPolling) {
        autoWorkflowPollStopRef.current = null;
      }
    };
  }, [
    autoWorkflowBackendStateUrl,
    autoWorkflowFullAutoKey,
    autoWorkflowStorageKey,
    clearAutoWorkflowState,
    projectId,
    setCurrentStyle,
  ]);

  const normalizeAutoWorkflowDoneStatus = (result, fallbackText = "任务已完成") => {
    if (result?.status === "stopped" || result?.stage === "stopped") {
      return { stage: "stopped", status: "stopped", stageText: result?.stageText || "已收到停止请求，当前服务商请求完成后会退出，可从已完成的位置重新开始", progress: result?.progress };
    }
    if (result?.status === "video_processing" || result?.stage === "video_processing") {
      return {
        stage: "video_processing",
        status: "video_processing",
        stageText: result?.stageText || "视频任务已提交，正在服务商侧生成",
        progress: result?.progress ?? 95,
      };
    }
    const nextStage = result?.requiresAssetConfirmation ? "asset_review" : "done";
    return {
      stage: nextStage,
      stageText: result?.requiresAssetConfirmation ? "资产已生成，请确认后继续" : fallbackText,
      progress: 100,
    };
  };

  const toggleAutoWorkflowFullAuto = useCallback(() => {
    const nextEnabled = !autoWorkflowFullAuto;
    setAutoWorkflowFullAutoState(nextEnabled);
    try {
      window.localStorage.setItem(autoWorkflowFullAutoKey, nextEnabled ? "true" : "false");
    } catch {
      // Ignore storage failures.
    }
    saveAutoWorkflowState({ fullAuto: nextEnabled });
    saveAutoWorkflowBackendSnapshot({ fullAuto: nextEnabled });
  }, [
    autoWorkflowFullAuto,
    autoWorkflowFullAutoKey,
    saveAutoWorkflowBackendSnapshot,
    saveAutoWorkflowState,
  ]);

  const createAutoWorkflowAbortController = () => {
    autoWorkflowAbortRef.current?.abort?.();
    const controller = new AbortController();
    autoWorkflowAbortRef.current = controller;
    return controller;
  };

  const stopAutoExplainerWorkflow = async () => {
    if (!projectId) return;
    const taskId = autoWorkflowTaskRef.current || autoWorkflowStatus?.taskId || autoWorkflowResult?.taskId || "";
    autoWorkflowAbortRef.current?.abort?.();
    autoWorkflowPollStopRef.current?.();
    autoWorkflowPollStopRef.current = null;
    autoWorkflowContinueInFlightRef.current = false;
    autoWorkflowTaskRef.current = null;
    setAutoWorkflowFullAutoState(false);
    try {
      window.localStorage.setItem(autoWorkflowFullAutoKey, "false");
    } catch {
      // Ignore storage failures.
    }
    setAutoWorkflowLoading(false);
    const stoppedStageText = "已收到停止请求，当前服务商请求完成后会退出，可从已完成的位置重新开始";
    const stoppedStatus = {
      stage: "stopped",
      status: "stopped",
      stageText: stoppedStageText,
      progress: autoWorkflowStatus?.progress,
      taskId,
    };
    const stoppedResult = autoWorkflowResult
      ? { ...autoWorkflowResult, status: "stopped", stage: "stopped", stageText: stoppedStageText }
      : autoWorkflowResult;
    setAutoWorkflowStatus(stoppedStatus);
    if (stoppedResult) {
      setAutoWorkflowResult(stoppedResult);
    }
    saveAutoWorkflowState({ status: stoppedStatus, result: stoppedResult, fullAuto: false });
    saveAutoWorkflowBackendSnapshot({
      taskId,
      status: "stopped",
      stage: "stopped",
      stageText: stoppedStatus.stageText,
      progress: stoppedStatus.progress,
      fullAuto: false,
      result: stoppedResult,
    });
    try {
      const stoppedState = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${projectId}/one-click-explainer/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, workflowStateKey: autoWorkflowStateKey }),
      });
      if (stoppedState?.result || stoppedState?.episodeResults || stoppedState?.episodes) {
        const result = stoppedState.result || {
          ...stoppedState,
          episodes: Array.isArray(stoppedState.episodes) ? stoppedState.episodes : stoppedState.episodeResults,
        };
        const stoppedBackendResult = { ...result, status: "stopped", stage: "stopped", stageText: stoppedStageText };
        setAutoWorkflowResult(stoppedBackendResult);
        saveAutoWorkflowState({ result: stoppedBackendResult, status: stoppedStatus, fullAuto: false });
      }
      toast.success("已收到停止请求，后端会在当前服务商请求完成后退出");
    } catch (error) {
      toast.error(error.message || "停止失败");
    }
  };

  const resumeAutoExplainerWorkflow = async () => {
    if (autoWorkflowNeedsConfirmation || autoWorkflowEpisodeIds.length > 0) {
      await continueAfterAssetConfirmation({ allowStopped: true });
      return;
    }
    if (sourceText.trim()) {
      await startAutoExplainerWorkflow({ keepCompletedState: true });
      return;
    }
    await startAllMiniStoryboardVideosWorkflow();
  };

  const startAutoExplainerWorkflow = async (options = {}) => {
    const normalizedOptions = options && typeof options.preventDefault === "function" ? {} : options;
    const {
      keepCompletedState = false,
      useExistingScript = false,
    } = normalizedOptions || {};
    if (!projectId) {
      toast.error("缺少项目ID");
      return;
    }
    if (!isSeedanceVideoProvider(autoWorkflowVideoProvider)) {
      toast.error("项目视频厂商配置尚未加载，请刷新项目后重试");
      return;
    }
    const submittedNovelText = useExistingScript ? "" : sourceText.trim();
    if (!useExistingScript && !isDialogueAutoEntry && !submittedNovelText) {
      toast.error("请先粘贴小说原文");
      return;
    }
    setAutoWorkflowLoading(true);
    setAutoWorkflowResult(null);
    if (!keepCompletedState) {
      clearAutoWorkflowState();
    }
    const initialStatus = { stageText: "正在提交资产准备任务", progress: 0 };
    setAutoWorkflowStatus(initialStatus);
    const abortController = createAutoWorkflowAbortController();
    try {
      const { result } = await runTaskToCompletion("NOVEL_EXPLAINER_ASSET_PREPARE_WORKFLOW", {
        projectId: Number(projectId),
        novelText: submittedNovelText,
        preferExistingEpisodes: true,
        useExistingEpisodesOnly: useExistingScript,
        workflowStateKey: autoWorkflowStateKey,
        workflowMode: isDialogueAutoEntry ? "dialogue_short_drama_auto" : "novel_explainer",
        outputStyle: isDialogueAutoEntry
          ? `${selectedAutoStyle?.label || "现代短剧"}对话短剧`
          : `${selectedAutoStyle?.label || "现代短剧"}解说剧`,
        scriptTitle: isDialogueAutoEntry ? "对话短剧自动流" : "一键解说剧",
        assetWorkflowStyle: isDialogueAutoEntry ? "dialogue-short-drama-assets" : "explainer-comic-assets",
        userRequest: isDialogueAutoEntry
          ? "对话短剧自动流：如果没有现有剧本，先把小说改写成正式对话短剧。人物动作必须体现“人物+正在做的事情+下意识反应+情绪落点”，不要机械化动作。"
          : "",
        styleAnchor: autoWorkflowStyle,
        textProvider: autoWorkflowTextProvider,
        imageProvider: autoWorkflowImageProvider,
        targetEpisodes: 0,
        maxEpisodes: 0,
        aspectRatio: autoWorkflowVideoRatio,
        videoProvider: autoWorkflowVideoProvider,
        videoModel: autoWorkflowVideoModel,
        resolution: autoWorkflowVideoResolution,
        segmentSeconds: autoWorkflowSegmentSeconds,
        generateVideos: false,
        waitForVideos: false,
        fullAuto: autoWorkflowFullAuto,
        workflowTrace: autoWorkflowTraceEnabled,
        workflowTraceId: `${autoWorkflowStateKey}:${projectId}:${Date.now()}`,
      }, {
        projectId: Number(projectId),
        provider: autoWorkflowTextProvider,
        source: isDialogueAutoEntry ? "dialogue_short_drama_asset_prepare_workflow" : "novel_explainer_asset_prepare_workflow",
        styleAnchor: autoWorkflowStyle,
        timeoutMs: 12 * 60 * 60 * 1000,
        intervalMs: 5000,
        signal: abortController.signal,
        onSubmitted: (task) => {
          autoWorkflowTaskRef.current = task.taskId;
          saveAutoWorkflowBackendSnapshot({
            taskId: task.taskId,
            taskType: "NOVEL_EXPLAINER_ASSET_PREPARE_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "资产准备任务已提交",
            progress: 0,
            result: null,
          });
          setAutoWorkflowStatus({
            taskId: task.taskId,
            taskType: "NOVEL_EXPLAINER_ASSET_PREPARE_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "资产准备任务已提交",
            progress: 0,
          });
        },
        onProgress: (status) => {
          setAutoWorkflowStatus({
            taskId: autoWorkflowTaskRef.current,
            stage: status?.currentStage || status?.stage,
            stageText: status?.currentStageText || status?.stageText || status?.status || "任务执行中",
            progress: status?.progress,
          });
        },
      });
      const doneStatus = normalizeAutoWorkflowDoneStatus(result, autoWorkflowCopy.assetDoneText);
      setAutoWorkflowResult(result);
      setAutoWorkflowStatus(doneStatus);
      saveAutoWorkflowState({ result, status: doneStatus });
      saveAutoWorkflowBackendSnapshot({
        taskId: autoWorkflowTaskRef.current,
        taskType: "NOVEL_EXPLAINER_ASSET_PREPARE_WORKFLOW",
        status: doneStatus.stage,
        stage: doneStatus.stage,
        stageText: doneStatus.stageText,
        progress: doneStatus.progress,
        result,
      });
      if (doneStatus.stage === "stopped") {
        toast.success(doneStatus.stageText || "已收到停止请求，当前服务商请求完成后会退出");
      } else if (autoWorkflowFullAuto) {
        const nextPayloadEpisodeIds = Array.isArray(result?.nextPayload?.episodeIds)
          ? result.nextPayload.episodeIds.filter(Boolean)
          : [];
        const nextEpisodeIds = Array.isArray(result?.episodes)
          ? result.episodes.map((episode) => episode?.episodeId).filter(Boolean)
          : nextPayloadEpisodeIds;
        if (nextEpisodeIds.length) {
          toast.success("全自动已开启，资产确认已跳过，继续生成视频");
          await continueAfterAssetConfirmation({ sourceResult: result, episodeIds: nextEpisodeIds });
        } else {
          toast.success("资产已生成，但没有可继续处理的集数");
        }
      } else {
        toast.success("资产已生成，请确认后继续");
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error(error.message || autoWorkflowCopy.taskErrorText, 0);
      setAutoWorkflowStatus({ stageText: error.message || "任务失败", progress: 0 });
    } finally {
      setAutoWorkflowLoading(false);
      if (autoWorkflowAbortRef.current === abortController) {
        autoWorkflowAbortRef.current = null;
      }
    }
  };

  const continueAfterAssetConfirmation = async ({ sourceResult = autoWorkflowResult, episodeIds = autoWorkflowEpisodeIds, allowStopped = false } = {}) => {
    if (autoWorkflowStopped && !allowStopped) {
      toast.error("自动流已停止，请点击重新开始后再继续");
      return;
    }
    if (autoWorkflowContinueInFlightRef.current) {
      return;
    }
    if (!projectId) {
      toast.error("缺少项目ID");
      return;
    }
    if (!isSeedanceVideoProvider(autoWorkflowVideoProvider)) {
      toast.error("项目视频厂商配置尚未加载，请刷新项目后重试");
      return;
    }
    const availableEpisodeIds = Array.isArray(episodeIds) && episodeIds.length ? episodeIds : autoWorkflowEpisodeIds;
    if (!availableEpisodeIds.length) {
      toast.error("没有可继续处理的集数");
      return;
    }
    autoWorkflowContinueInFlightRef.current = true;
    const nextPayload = sourceResult?.nextPayload && typeof sourceResult.nextPayload === "object"
      ? sourceResult.nextPayload
      : {};
    setAutoWorkflowLoading(true);
    const initialStatus = { stageText: "正在提交小故事板和视频生成任务", progress: 0 };
    setAutoWorkflowStatus(initialStatus);
    saveAutoWorkflowState({ status: initialStatus });
    const abortController = createAutoWorkflowAbortController();
    try {
      const payload = {
        ...nextPayload,
        projectId: Number(projectId),
        workflowStateKey: autoWorkflowStateKey,
        workflowMode: isDialogueAutoEntry ? "dialogue_short_drama_auto" : "novel_explainer",
        episodeIds: Array.isArray(nextPayload.episodeIds) && nextPayload.episodeIds.length ? nextPayload.episodeIds : availableEpisodeIds,
        regenerateMiniStoryboard: Object.prototype.hasOwnProperty.call(nextPayload, "regenerateMiniStoryboard")
          ? Boolean(nextPayload.regenerateMiniStoryboard)
          : false,
        generateAssets: false,
        generateVideos: true,
        waitForVideos: true,
        skipExistingVideos: true,
        prepareCharacterAssets: false,
        aspectRatio: autoWorkflowVideoRatio,
        videoProvider: autoWorkflowVideoProvider,
        videoModel: autoWorkflowVideoModel,
        resolution: autoWorkflowVideoResolution,
        segmentSeconds: autoWorkflowSegmentSeconds,
        styleAnchor: autoWorkflowStyle,
        textProvider: autoWorkflowTextProvider,
        imageProvider: autoWorkflowImageProvider,
        fullAuto: autoWorkflowFullAuto,
        workflowTrace: autoWorkflowTraceEnabled,
        workflowTraceId: `${autoWorkflowStateKey}:${projectId}:${Date.now()}`,
      };
      const { result } = await runTaskToCompletion("MINI_STORYBOARD_ALL_VIDEO_WORKFLOW", payload, {
        projectId: Number(projectId),
        provider: autoWorkflowTextProvider,
        source: isDialogueAutoEntry ? "dialogue_short_drama_mini_storyboard_all_video_workflow" : "mini_storyboard_all_video_workflow",
        styleAnchor: autoWorkflowStyle,
        timeoutMs: 12 * 60 * 60 * 1000,
        intervalMs: 5000,
        signal: abortController.signal,
        onSubmitted: (task) => {
          autoWorkflowTaskRef.current = task.taskId;
          saveAutoWorkflowBackendSnapshot({
            taskId: task.taskId,
            taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "小故事板和视频生成任务已提交",
            progress: 0,
            result: sourceResult,
          });
          setAutoWorkflowStatus({
            taskId: task.taskId,
            taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "小故事板和视频生成任务已提交",
            progress: 0,
          });
        },
        onProgress: (status) => {
          setAutoWorkflowStatus({
            taskId: autoWorkflowTaskRef.current,
            stage: status?.currentStage || status?.stage,
            stageText: status?.currentStageText || status?.stageText || status?.status || "任务执行中",
            progress: status?.progress,
          });
        },
      });
      const doneStatus = normalizeAutoWorkflowDoneStatus(result, "小故事板和视频生成已完成");
      setAutoWorkflowResult(result);
      setAutoWorkflowStatus(doneStatus);
      saveAutoWorkflowState({ result, status: doneStatus });
      saveAutoWorkflowBackendSnapshot({
        taskId: autoWorkflowTaskRef.current,
        taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
        status: doneStatus.stage,
        stage: doneStatus.stage,
        stageText: doneStatus.stageText,
        progress: doneStatus.progress,
        result,
      });
      if (doneStatus.stage === "stopped") {
        toast.success(doneStatus.stageText || "已收到停止请求，当前服务商请求完成后会退出");
      } else if (doneStatus.stage === "video_processing") {
        toast.success(doneStatus.stageText || "视频任务已提交，正在服务商侧生成");
      } else {
        toast.success("小故事板和视频生成已完成");
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error(error.message || "小故事板和视频生成失败", 0);
      setAutoWorkflowStatus({ stageText: error.message || "任务失败", progress: 0 });
    } finally {
      autoWorkflowContinueInFlightRef.current = false;
      setAutoWorkflowLoading(false);
      if (autoWorkflowAbortRef.current === abortController) {
        autoWorkflowAbortRef.current = null;
      }
    }
  };

  const startAllMiniStoryboardVideosWorkflow = async () => {
    if (!projectId) {
      toast.error("缺少项目ID");
      return;
    }
    if (!isSeedanceVideoProvider(autoWorkflowVideoProvider)) {
      toast.error("项目视频厂商配置尚未加载，请刷新项目后重试");
      return;
    }
    setAutoWorkflowLoading(true);
    setAutoWorkflowResult(null);
    clearAutoWorkflowState();
    const initialStatus = { stageText: "正在提交全量小故事板视频任务", progress: 0 };
    setAutoWorkflowStatus(initialStatus);
    const abortController = createAutoWorkflowAbortController();
    try {
      const { result } = await runTaskToCompletion("MINI_STORYBOARD_ALL_VIDEO_WORKFLOW", {
        projectId: Number(projectId),
        workflowStateKey: autoWorkflowStateKey,
        workflowMode: isDialogueAutoEntry ? "dialogue_short_drama_auto" : "novel_explainer",
        regenerateMiniStoryboard: false,
        generateAssets: false,
        generateVideos: true,
        waitForVideos: true,
        skipExistingVideos: true,
        prepareCharacterAssets: false,
        aspectRatio: autoWorkflowVideoRatio,
        videoProvider: autoWorkflowVideoProvider,
        videoModel: autoWorkflowVideoModel,
        resolution: autoWorkflowVideoResolution,
        segmentSeconds: autoWorkflowSegmentSeconds,
        styleAnchor: autoWorkflowStyle,
        textProvider: autoWorkflowTextProvider,
        imageProvider: autoWorkflowImageProvider,
        fullAuto: autoWorkflowFullAuto,
        workflowTrace: autoWorkflowTraceEnabled,
        workflowTraceId: `${autoWorkflowStateKey}:${projectId}:${Date.now()}`,
      }, {
        projectId: Number(projectId),
        provider: autoWorkflowTextProvider,
        source: isDialogueAutoEntry ? "dialogue_short_drama_mini_storyboard_all_video_workflow" : "mini_storyboard_all_video_workflow",
        styleAnchor: autoWorkflowStyle,
        timeoutMs: 12 * 60 * 60 * 1000,
        intervalMs: 5000,
        signal: abortController.signal,
        onSubmitted: (task) => {
          autoWorkflowTaskRef.current = task.taskId;
          saveAutoWorkflowBackendSnapshot({
            taskId: task.taskId,
            taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "全量小故事板视频任务已提交",
            progress: 0,
            result: null,
          });
          setAutoWorkflowStatus({
            taskId: task.taskId,
            taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
            status: "processing",
            stage: "submitted",
            stageText: "全量小故事板视频任务已提交",
            progress: 0,
          });
        },
        onProgress: (status) => {
          setAutoWorkflowStatus({
            taskId: autoWorkflowTaskRef.current,
            stage: status?.currentStage || status?.stage,
            stageText: status?.currentStageText || status?.stageText || status?.status || "任务执行中",
            progress: status?.progress,
          });
        },
      });
      const doneStatus = normalizeAutoWorkflowDoneStatus(result, "全量小故事板视频任务已完成");
      setAutoWorkflowResult(result);
      setAutoWorkflowStatus(doneStatus);
      saveAutoWorkflowState({ result, status: doneStatus });
      saveAutoWorkflowBackendSnapshot({
        taskId: autoWorkflowTaskRef.current,
        taskType: "MINI_STORYBOARD_ALL_VIDEO_WORKFLOW",
        status: doneStatus.stage,
        stage: doneStatus.stage,
        stageText: doneStatus.stageText,
        progress: doneStatus.progress,
        result,
      });
      if (doneStatus.stage === "stopped") {
        toast.success(doneStatus.stageText || "已收到停止请求，当前服务商请求完成后会退出");
      } else if (doneStatus.stage === "video_processing") {
        toast.success(doneStatus.stageText || "视频任务已提交，正在服务商侧生成");
      } else {
        toast.success("全量小故事板视频任务已完成");
      }
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error(error.message || "全量小故事板视频生成失败", 0);
      setAutoWorkflowStatus({ stageText: error.message || "任务失败", progress: 0 });
    } finally {
      setAutoWorkflowLoading(false);
      if (autoWorkflowAbortRef.current === abortController) {
        autoWorkflowAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (
      autoWorkflowStopped
      || autoWorkflowRecoveredFromProject
      || !autoWorkflowFullAuto
      || autoWorkflowLoading
      || !autoWorkflowNeedsConfirmation
      || !autoWorkflowEpisodeIds.length
    ) return;
    continueAfterAssetConfirmation();
  }, [
    autoWorkflowEpisodeIds.length,
    autoWorkflowFullAuto,
    autoWorkflowLoading,
    autoWorkflowNeedsConfirmation,
    autoWorkflowRecoveredFromProject,
    autoWorkflowStopped,
  ]);

  const triggerAutoWorkflowDownload = useCallback((url, filename) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  const downloadAllAutoWorkflowEpisodeZips = useCallback(async () => {
    if (!projectId) {
      toast.error("缺少项目ID");
      return;
    }
    const episodes = autoWorkflowEpisodes.filter((episode) => episode?.episodeId || episode?.id);
    if (!episodes.length) {
      toast.warning("还没有可下载的集数");
      return;
    }
    setAutoWorkflowDownloading(true);
    let successCount = 0;
    let skippedCount = 0;
    let partialCount = 0;
    const failures = [];
    try {
      for (const episode of episodes) {
        const episodeIdValue = episode.episodeId || episode.id;
        const episodeName = buildEpisodeDownloadLabel(episode, episodeIdValue);
        const params = new URLSearchParams({
          projectId: String(projectId),
          episodeId: String(episodeIdValue),
          dramaName: `项目${projectId || ""}`,
          episodeName,
        });
        try {
          const { blob, hasFailureNote } = await fetchVerifiedVideoZip(`${API_HOST}/api/episode-mini-storyboard/videos/download?${params.toString()}`, {
            headers: downloadHeaders(),
          });
          const objectUrl = URL.createObjectURL(blob);
          const fileName = `${sanitizeFileNamePart(`项目${projectId || ""}`)}_${sanitizeFileNamePart(episodeName)}_小故事板视频.zip`;
          triggerAutoWorkflowDownload(objectUrl, fileName);
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
          successCount += 1;
          if (hasFailureNote) partialCount += 1;
          await new Promise((resolve) => window.setTimeout(resolve, 300));
        } catch (error) {
          if (error.status === 404) skippedCount += 1;
          else failures.push(`${episodeName}：${error.message || "下载失败"}`);
        }
      }
      if (successCount > 0) {
        toast.success(`已触发 ${successCount} 集视频压缩包下载${skippedCount ? `，${skippedCount} 集暂无可下载视频` : ""}`);
        if (partialCount) toast.warning(`${partialCount} 集有部分视频下载失败，请查看压缩包内的下载失败说明`);
      } else if (!failures.length) {
        toast.warning("当前没有可下载的小故事板视频压缩包");
      }
      if (failures.length) {
        toast.error(`压缩包下载失败：${failures.slice(0, 3).join("；")}${failures.length > 3 ? `；另有 ${failures.length - 3} 集失败` : ""}`, 0);
      }
    } finally {
      setAutoWorkflowDownloading(false);
    }
  }, [autoWorkflowEpisodes, projectId, toast, triggerAutoWorkflowDownload]);

  const autoAssetCounts = {
    characters: Number(autoWorkflowAssetSummary.characters || 0),
    scenes: Number(autoWorkflowAssetSummary.scenes || 0),
    props: Number(autoWorkflowAssetSummary.props || 0),
  };
  const autoAssetTotal = autoAssetCounts.characters + autoAssetCounts.scenes + autoAssetCounts.props;
  const autoScriptReady = autoWorkflowEpisodeCount > 0;
  const autoAssetReady = autoAssetTotal > 0;
  const autoProductionStarted = autoWorkflowVideoCounts.total > 0
    || ["gua_asset_prepare", "mini_storyboard", "video_submit", "video_wait", "video_retry"].includes(autoWorkflowStage);
  const autoProductionDone = autoWorkflowIsDone;
  const autoDeliveryReady = autoWorkflowIsDone || autoWorkflowVideoCounts.done > 0;
  const stageCardStyle = (state) => {
    if (state === "done") return { borderColor: "color-mix(in srgb, var(--color-success) 50%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-success) 8%, transparent)" };
    if (state === "active") return { borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" };
    if (state === "error") return { borderColor: "color-mix(in srgb, var(--color-error) 65%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 8%, transparent)" };
    return { borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft };
  };
  const statusDotStyle = (state) => {
    if (state === "done") return { backgroundColor: "var(--color-success)", color: "var(--color-accent-contrast)" };
    if (state === "active") return { backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" };
    if (state === "error") return { backgroundColor: "var(--color-error)", color: "var(--color-accent-contrast)" };
    return { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" };
  };
  const stageStatusText = (state) => {
    if (state === "done") return "已完成";
    if (state === "active") return "进行中";
    if (state === "error") return "需处理";
    return "未开始";
  };
  const autoStageCards = [
    {
      index: 1,
      title: autoWorkflowCopy.firstStageTitle,
      state: autoScriptReady ? "done" : isDialogueAutoEntry ? "active" : sourceText.trim() ? "active" : "idle",
      lines: [
        autoScriptReady ? autoWorkflowCopy.firstStageDone : autoWorkflowCopy.firstStageActive,
        autoScriptReady ? "可进入资产确认" : "未检测到可用分集剧本",
        autoWorkflowCopy.firstStageThirdLine,
      ],
    },
    {
      index: 2,
      title: "资产确认",
      state: autoWorkflowNeedsConfirmation ? "active" : autoAssetReady ? "done" : "idle",
      lines: [
        `角色 ${autoAssetCounts.characters}`,
        `场景 ${autoAssetCounts.scenes}`,
        `道具 ${autoAssetCounts.props}`,
      ],
    },
    {
      index: 3,
      title: "自动生产",
      state: autoProductionDone ? "done" : autoWorkflowLoading && !autoWorkflowNeedsConfirmation ? "active" : autoWorkflowStopped ? "error" : autoProductionStarted ? "active" : "idle",
      lines: [
        autoProductionStarted ? "角色入库/小故事板已进入自动阶段" : "等待资产确认",
        autoWorkflowVideoCounts.total ? `视频 ${autoWorkflowVideoCounts.done}/${autoWorkflowVideoCounts.total}` : "视频未提交",
        autoWorkflowStopped ? "已停止，可断点继续" : autoWorkflowStageText,
      ],
    },
    {
      index: 4,
      title: "完成与交付",
      state: autoWorkflowIsDone ? "done" : autoDeliveryReady ? "active" : "idle",
      lines: [
        autoDeliveryReady ? "已有可下载视频" : "等待视频完成",
        autoWorkflowEpisodes.length ? `覆盖 ${autoWorkflowEpisodes.length} 集` : "暂无集数",
        "每集单独打包为 zip",
      ],
    },
  ];
  const autoWorkflowShellConfig = isAutoWorkflowEntry
    ? {
      ...config,
      title: autoWorkflowCopy.title,
      subtitle: isDialogueAutoEntry ? "剧本到视频自动生产" : "小说到旁白剧自动生产",
    }
    : config;

  if (isAutoWorkflowEntry && sourceType === "novel_text") {
    return (
      <FlowShell activeStep={1} session={session} onCreateSession={onCreateSession} creating={loading === "create"} mockAgentEnabled={mockAgentEnabled} onToggleMockAgent={onToggleMockAgent} config={autoWorkflowShellConfig} hideFlowControls>
        <div className="max-w-[1500px] mx-auto rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div className="px-8 py-5 border-b flex items-center justify-between gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Workflow size={20} style={{ color: theme.colors.accentPrimary }} />
                <div className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }}>{autoWorkflowCopy.title}</div>
              </div>
              <div className="text-sm leading-6 mt-1" style={{ color: "var(--color-text-secondary)" }}>
                {autoWorkflowCopy.description}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={toggleAutoWorkflowFullAuto}
                className="h-9 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 border"
                style={{
                  borderColor: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-border-primary)",
                  backgroundColor: autoWorkflowFullAuto ? "var(--color-accent-light)" : SURFACE.panel,
                  color: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-text-primary)",
                }}
                title="开启后跳过资产确认，自动继续生成小故事板和视频"
              >
                {autoWorkflowFullAuto ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                全自动{autoWorkflowFullAuto ? "开" : "关"}
              </button>
              {autoWorkflowLoading && (
                <button
                  type="button"
                  onClick={stopAutoExplainerWorkflow}
                  className="h-9 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 border"
                  style={{ borderColor: "rgba(239,68,68,0.65)", backgroundColor: "rgba(239,68,68,0.12)", color: "var(--color-danger, #ef4444)" }}
                >
                  <Square size={14} />
                  停止
                </button>
              )}
              {canResumeAutoWorkflow && (
                <button
                  type="button"
                  onClick={resumeAutoExplainerWorkflow}
                  className="h-9 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 border"
                  style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
                >
                  <RefreshCw size={14} />
                  从断点继续
                </button>
              )}
              <button
                type="button"
                onClick={downloadAllAutoWorkflowEpisodeZips}
                disabled={autoWorkflowDownloading || !autoWorkflowEpisodes.length}
                className="h-9 px-3 rounded-md text-sm font-semibold flex items-center gap-1.5 border disabled:opacity-50"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}
                title={autoWorkflowEpisodes.length ? "按集下载小故事板视频压缩包" : "暂无可下载集数"}
              >
                {autoWorkflowDownloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                一键下载
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <section className="rounded-md border p-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>真实进度</div>
                  <div className="text-sm leading-6 mt-1" style={{ color: "var(--color-text-muted)" }}>
                    {autoWorkflowStageText}。{autoWorkflowCopy.progressHint}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 min-w-[360px]">
                  <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>分集剧本</div>
                    <div className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>{autoWorkflowEpisodeCount}</div>
                  </div>
                  <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>资产</div>
                    <div className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>{autoAssetTotal}</div>
                  </div>
                  <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>视频</div>
                    <div className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>
                      {autoWorkflowVideoCounts.total ? `${autoWorkflowVideoCounts.done}/${autoWorkflowVideoCounts.total}` : "0"}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-4 gap-4">
              {autoStageCards.map((card) => (
                <div key={card.index} className="rounded-md border p-4 min-h-[220px] flex flex-col" style={stageCardStyle(card.state)}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>{card.title}</div>
                      <div className="text-sm mt-1" style={{ color: "var(--color-text-muted)" }}>{stageStatusText(card.state)}</div>
                    </div>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold" style={statusDotStyle(card.state)}>
                      {card.index}
                    </span>
                  </div>
                  <div className="mt-5 space-y-2 flex-1">
                    {card.lines.map((line, index) => (
                      <div key={`${card.index}-${index}`} className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
                        {card.state === "done" && index === 0 ? "✓ " : card.state === "active" && index === 0 ? "● " : "○ "}
                        {line}
                      </div>
                    ))}
                  </div>

                  {card.index === 1 && (
                    <div className="mt-4 space-y-3">
                      <textarea
                        value={sourceText}
                        onChange={(e) => onSourceTextChange(e.target.value)}
                        rows={7}
                        className="w-full rounded-md border p-3 text-sm leading-6 outline-none resize-none"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                        placeholder={autoWorkflowCopy.sourcePlaceholder}
                      />
                      {isDialogueAutoEntry ? (
                        <div className="rounded-md border p-3 text-xs leading-5 flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel, color: "var(--color-text-muted)" }}>
                          <span>已有分集剧本时可留空，系统会优先复用剧本中心内容。</span>
                          <button
                            type="button"
                            onClick={() => window.open(`/project/${projectId}/script-analysis`, "_blank")}
                            className="h-8 shrink-0 px-3 rounded-md border text-sm font-semibold"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
                          >
                            打开剧本中心
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                          如果剧本中心已经拆出分集，可不粘贴小说，系统会直接复用现有分集生成资产。
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-2">
                        <AppSelect
                          value={autoWorkflowStyle}
                          onChange={(e) => setAutoWorkflowStyle(e.target.value)}
                          className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                        >
                          {STYLE_OPTIONS.filter((item) => item.value !== "custom").map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </AppSelect>
                        <div className="grid grid-cols-2 gap-2">
                          <AppSelect
                            value={autoWorkflowTextProvider}
                            onChange={(e) => setAutoWorkflowTextProvider(e.target.value)}
                            className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                          >
                            {ONE_CLICK_TEXT_MODEL_OPTIONS.map((item) => (
                              <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                          </AppSelect>
                          <AppSelect
                            value={autoWorkflowImageProvider}
                            onChange={(e) => setAutoWorkflowImageProvider(e.target.value)}
                            className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                          >
                            {ONE_CLICK_IMAGE_MODEL_OPTIONS.map((item) => (
                              <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                          </AppSelect>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={toggleAutoWorkflowFullAuto}
                        className="h-9 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border"
                        style={{
                          borderColor: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-border-primary)",
                          backgroundColor: autoWorkflowFullAuto ? "var(--color-accent-light)" : SURFACE.panel,
                          color: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-text-primary)",
                        }}
                        title="开启后跳过资产确认，自动继续生成小故事板和视频"
                      >
                        {autoWorkflowFullAuto ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        全自动{autoWorkflowFullAuto ? "已开启" : "默认关闭"}
                      </button>
                      <button
                        type="button"
                        onClick={() => startAutoExplainerWorkflow({ useExistingScript: !sourceText.trim() })}
                        disabled={!canRunAutoWorkflow}
                        className="h-10 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                        style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                      >
                        {autoWorkflowLoading && !autoWorkflowNeedsConfirmation ? <Loader2 size={15} className="animate-spin" /> : <Workflow size={15} />}
                        {sourceText.trim() ? autoWorkflowCopy.startButton : "复用剧本中心集数生成资产"}
                      </button>
                      {sourceText.trim() && (
                        <button
                          type="button"
                          onClick={() => startAutoExplainerWorkflow({ useExistingScript: true })}
                          disabled={!canRunExistingEpisodeWorkflow}
                          className="h-9 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}
                          title="忽略当前文本框内容，直接使用剧本中心已拆分的集数生成资产"
                        >
                          <CheckCircle2 size={15} />
                          复用剧本中心集数生成资产
                        </button>
                      )}
                    </div>
                  )}

                  {card.index === 2 && (
                    <div className="mt-4 space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ["character", "角色", autoAssetCounts.characters],
                          ["scene", "场景", autoAssetCounts.scenes],
                          ["prop", "道具", autoAssetCounts.props],
                        ].map(([assetType, label, count]) => (
                          <button
                            key={assetType}
                            type="button"
                            onClick={() => window.open(`/project/${projectId}/assets/${assetType}`, "_blank")}
                            className="rounded-md border px-2 py-2 text-sm"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}
                          >
                            {label} {count}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={continueAfterAssetConfirmation}
                        disabled={!canContinueAutoWorkflow}
                        className="h-10 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                        style={{ borderColor: "var(--color-success)", backgroundColor: autoWorkflowNeedsConfirmation ? "rgba(34,197,94,0.14)" : SURFACE.panel, color: autoWorkflowNeedsConfirmation ? "var(--color-success)" : "var(--color-text-muted)" }}
                      >
                        {autoWorkflowLoading && autoWorkflowNeedsConfirmation ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                        确认资产并继续
                      </button>
                    </div>
                  )}

                  {card.index === 3 && (
                    <div className="mt-4 space-y-3">
                      {autoWorkflowStopped ? (
                        <button
                          type="button"
                          onClick={resumeAutoExplainerWorkflow}
                          disabled={!canResumeAutoWorkflow}
                          className="h-10 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                          style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
                        >
                          <RefreshCw size={15} />
                          从断点继续
                        </button>
                      ) : autoWorkflowLoading ? (
                        <button
                          type="button"
                          onClick={stopAutoExplainerWorkflow}
                          className="h-10 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border"
                          style={{ borderColor: "rgba(239,68,68,0.65)", backgroundColor: "rgba(239,68,68,0.12)", color: "var(--color-danger, #ef4444)" }}
                        >
                          <Square size={15} />
                          停止
                        </button>
                      ) : (
                        <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel, color: "var(--color-text-muted)" }}>
                          资产确认后自动运行。已成功的视频会跳过，失败项会按策略重试。
                        </div>
                      )}
                    </div>
                  )}

                  {card.index === 4 && (
                    <div className="mt-4 space-y-3">
                      <button
                        type="button"
                        onClick={downloadAllAutoWorkflowEpisodeZips}
                        disabled={autoWorkflowDownloading || !autoWorkflowEpisodes.length}
                        className="h-10 w-full rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                        style={{ borderColor: theme.colors.accentPrimary, backgroundColor: autoDeliveryReady ? theme.colors.accentPrimary : SURFACE.panel, color: autoDeliveryReady ? "white" : "var(--color-text-muted)" }}
                      >
                        {autoWorkflowDownloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        一键下载每集 zip
                      </button>
                      <div className="text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                        点击一次会按集依次下载，每一集保持独立压缩包。
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </section>
          </div>
        </div>
      </FlowShell>
    );
  }

  return (
    <FlowShell activeStep={1} session={session} onCreateSession={onCreateSession} creating={loading === "create"} mockAgentEnabled={mockAgentEnabled} onToggleMockAgent={onToggleMockAgent} config={config}>
      <div className="max-w-[1280px] mx-auto rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
        <div className="px-8 py-6 border-b flex items-center justify-between gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
          <div className="min-w-0">
            <div className="text-2xl font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>{config?.heroTitle || "建立一个可生产的短剧项目"}</div>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
              {config?.heroDescription || "这里先完成选题、改编方案、短剧设定、风格锚点和分集大纲；单集剧本、分镜和视频提示词会进入分集生产台继续做。"}
            </div>
          </div>
          <span className="px-3 py-1.5 rounded-md text-sm font-semibold shrink-0" style={TONE_STYLE.accent}>
            第 1 步
          </span>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-6 p-6">
          <section className="rounded-md border p-5 space-y-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
            <div>
              <div className="text-xl font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>{config?.sourceTitle || "先确定项目来源"}</div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>{config?.sourceHint || "选题只是立项入口，不会直接跳到写单集剧本。"}</div>
            </div>

            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(sourceTypes.length, 3)}, minmax(0, 1fr))` }}>
              {sourceTypes.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => onSourceTypeChange(source.id)}
                  className="h-14 rounded-md border px-4 flex items-center gap-3 text-left cursor-pointer"
                  style={{
                    borderColor: sourceType === source.id ? theme.colors.accentPrimary : "var(--color-border-primary)",
                    backgroundColor: sourceType === source.id ? "var(--color-accent-light)" : SURFACE.panel,
                    color: sourceType === source.id ? theme.colors.accentPrimary : "var(--color-text-primary)",
                  }}
                >
                  <source.icon size={18} />
                  <span className="text-base font-semibold">{source.label}</span>
                </button>
              ))}
            </div>

            {sourceType === "hot_news" ? (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Compass size={16} style={{ color: theme.colors.accentPrimary }} />
                    <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>常用题材入口</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {SOURCE_PRESETS.map((preset) => {
                      const active = sourceQuery === preset.query;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => onSourceQueryChange(preset.query)}
                          className="rounded-md border p-4 text-left space-y-2 cursor-pointer"
                          style={{
                            borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                            backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panel,
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>{preset.title}</div>
                            {active && <CheckCircle2 size={16} style={{ color: theme.colors.accentPrimary }} />}
                          </div>
                          <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>{preset.description}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-base font-semibold block" style={{ color: "var(--color-text-primary)" }}>搜索方向</label>
                  <div className="flex gap-3">
                    <input
                      value={sourceQuery}
                      onChange={(e) => onSourceQueryChange(e.target.value)}
                      className="h-11 flex-1 rounded-md border px-3 text-sm outline-none"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                      placeholder="例如：奇葩社会新闻 反转 热点"
                    />
                    <button
                      type="button"
                      onClick={onSearch}
                      disabled={searchDisabled}
                      className="h-11 px-5 rounded-md text-sm font-semibold flex items-center gap-2 border disabled:opacity-50"
                      style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                    >
                      {loading === "search" ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                      搜索候选
                    </button>
                  </div>
                </div>
              </>
            ) : sourceType === "ai_theme" ? (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={16} style={{ color: theme.colors.accentPrimary }} />
                    <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>常用主题入口</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {AI_THEME_PRESETS.map((preset) => {
                      const active = sourceQuery === preset.query;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => onSourceQueryChange(preset.query)}
                          className="rounded-md border p-4 text-left space-y-2 cursor-pointer"
                          style={{
                            borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                            backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panel,
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>{preset.title}</div>
                            {active && <CheckCircle2 size={16} style={{ color: theme.colors.accentPrimary }} />}
                          </div>
                          <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>{preset.description}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-base font-semibold block" style={{ color: "var(--color-text-primary)" }}>主题方向</label>
                  <div className="flex gap-3">
                    <input
                      value={sourceQuery}
                      onChange={(e) => onSourceQueryChange(e.target.value)}
                      className="h-11 flex-1 rounded-md border px-3 text-sm outline-none"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                      placeholder="例如：一个底层外卖员误入豪门秘密，最后反转逆袭"
                    />
                    <button
                      type="button"
                      onClick={onSearch}
                      disabled={searchDisabled}
                      className="h-11 px-5 rounded-md text-sm font-semibold flex items-center gap-2 border disabled:opacity-50"
                      style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                    >
                      {loading === "search" ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                      生成题材
                    </button>
                  </div>
                  <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
                    AI 会基于这个主题发散多个短剧题目，每个题目都带一句话故事、冲突、反转和连续剧潜力。
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <label className="text-base font-semibold block" style={{ color: "var(--color-text-primary)" }}>{config?.textLabel || "粘贴素材"}</label>
                <textarea
                  value={sourceText}
                  onChange={(e) => onSourceTextChange(e.target.value)}
                  rows={12}
                  className="w-full rounded-md border p-3 text-sm leading-6 outline-none resize-none"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                  placeholder={config?.textPlaceholder || "粘贴小说、梗概、原作设定或任意素材。"}
                />
                {sourceType === "novel_text" && (
                  <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>一键生成解说剧视频</div>
                          <div className="text-sm leading-6 mt-1" style={{ color: "var(--color-text-muted)" }}>
                          两步执行：先把小说改成解说剧并生成角色/场景/道具，确认资产后再自动角色入库、拆小故事板并提交 GuaVideo 480p 视频。
                          </div>
                        {isAutoExplainerEntry && (
                          <div className="mt-2 text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
                            如果项目已经有集数，也可以直接补齐所有集的小故事板和视频。
                          </div>
                        )}
                        <div className="mt-3 grid gap-3 md:grid-cols-3">
                          <label className="min-w-0">
                            <span className="text-xs font-semibold block mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                              解说剧视觉风格
                            </span>
                            <AppSelect
                              value={autoWorkflowStyle}
                              onChange={(e) => setAutoWorkflowStyle(e.target.value)}
                              className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                            >
                              {STYLE_OPTIONS.filter((item) => item.value !== "custom").map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                              ))}
                            </AppSelect>
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-semibold block mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                              文字模型
                            </span>
                            <AppSelect
                              value={autoWorkflowTextProvider}
                              onChange={(e) => setAutoWorkflowTextProvider(e.target.value)}
                              className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                            >
                              {ONE_CLICK_TEXT_MODEL_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                              ))}
                            </AppSelect>
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-semibold block mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
                              图片模型
                            </span>
                            <AppSelect
                              value={autoWorkflowImageProvider}
                              onChange={(e) => setAutoWorkflowImageProvider(e.target.value)}
                              className="h-9 w-full rounded-md border px-3 text-sm outline-none"
                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                            >
                              {ONE_CLICK_IMAGE_MODEL_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                              ))}
                            </AppSelect>
                          </label>
                          <div className="md:col-span-3 text-xs" style={{ color: "var(--color-text-muted)" }}>
                            用户选择是第一优先级；可选模型与左侧全局 AI 模型列表保持一致，列表外模型不会展示。视频固定 GuaVideo 480p。
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={toggleAutoWorkflowFullAuto}
                          className="mt-3 h-9 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 border"
                          style={{
                            borderColor: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-border-primary)",
                            backgroundColor: autoWorkflowFullAuto ? "var(--color-accent-light)" : SURFACE.panelSoft,
                            color: autoWorkflowFullAuto ? theme.colors.accentPrimary : "var(--color-text-primary)",
                          }}
                          title="开启后不再停在资产确认步骤，会自动继续生成小故事板和视频"
                        >
                          {autoWorkflowFullAuto ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                          全自动{autoWorkflowFullAuto ? "已开启" : "默认关闭"}
                        </button>
                      </div>
                      <div className="flex shrink-0 flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => startAutoExplainerWorkflow({ useExistingScript: !sourceText.trim() })}
                          disabled={!canRunAutoWorkflow}
                          className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                          style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                        >
                          {autoWorkflowLoading ? <Loader2 size={15} className="animate-spin" /> : <Workflow size={15} />}
                          {sourceText.trim() ? autoWorkflowCopy.startButton : "复用剧本中心集数"}
                        </button>
                        {sourceText.trim() && (
                          <button
                            type="button"
                            onClick={() => startAutoExplainerWorkflow({ useExistingScript: true })}
                            disabled={!canRunExistingEpisodeWorkflow}
                            className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
                            title="忽略当前文本框内容，直接使用剧本中心已拆分的集数生成资产"
                          >
                            <CheckCircle2 size={15} />
                            复用剧本中心集数
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={continueAfterAssetConfirmation}
                          disabled={!canContinueAutoWorkflow}
                          className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                          style={{ borderColor: "var(--color-success)", backgroundColor: autoWorkflowNeedsConfirmation ? "rgba(34,197,94,0.14)" : SURFACE.panelSoft, color: autoWorkflowNeedsConfirmation ? "var(--color-success)" : "var(--color-text-primary)" }}
                        >
                          {autoWorkflowLoading ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                          确认资产并继续
                        </button>
                        <button
                          type="button"
                          onClick={startAllMiniStoryboardVideosWorkflow}
                          disabled={!canRunExistingEpisodeWorkflow}
                          className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
                        >
                          {autoWorkflowLoading ? <Loader2 size={15} className="animate-spin" /> : <Workflow size={15} />}
                          补齐已有集数
                        </button>
                        {autoWorkflowLoading && (
                          <button
                            type="button"
                            onClick={stopAutoExplainerWorkflow}
                            className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border"
                            style={{ borderColor: "rgba(239,68,68,0.65)", backgroundColor: "rgba(239,68,68,0.12)", color: "var(--color-danger, #ef4444)" }}
                          >
                            <Square size={15} />
                            停止
                          </button>
                        )}
                        {canResumeAutoWorkflow && (
                          <button
                            type="button"
                            onClick={resumeAutoExplainerWorkflow}
                            className="h-10 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border"
                            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
                          >
                            <RefreshCw size={15} />
                            从断点继续
                          </button>
                        )}
                      </div>
                    </div>
                    {(autoWorkflowStatus || autoWorkflowResult) && (
                      <div className="rounded-md px-3 py-2 text-sm leading-6" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
                        <div className="flex items-center justify-between gap-3">
                          <span>{autoWorkflowStatus?.stageText || "任务已完成"}</span>
                          {Number.isFinite(Number(autoWorkflowStatus?.progress)) && (
                            <span style={{ color: theme.colors.accentPrimary }}>{Math.round(Number(autoWorkflowStatus.progress))}%</span>
                          )}
                        </div>
                        {autoWorkflowResult?.episodes?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {autoWorkflowResult.episodes.slice(0, 8).map((episode) => (
                              <button
                                key={episode.episodeId}
                                type="button"
                                onClick={() => {
                                  if (!autoWorkflowNeedsConfirmation) {
                                    window.open(`/project/${projectId}/episode/${episode.episodeId}/mini-storyboard`, "_blank");
                                  }
                                }}
                                className="px-2.5 py-1 rounded-md border text-sm"
                                style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)", backgroundColor: SURFACE.panelSoft }}
                              >
                                第{episode.sortOrder}集{autoWorkflowNeedsConfirmation ? "资产" : "小故事板"}
                              </button>
                            ))}
                          </div>
                        )}
                        {autoWorkflowResult?.assetSummary && (
                          <div className="mt-3 rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span>资产已生成：</span>
                              <span style={{ color: "var(--color-text-primary)" }}>角色 {autoWorkflowResult.assetSummary.characters || 0}</span>
                              <span style={{ color: "var(--color-text-primary)" }}>场景 {autoWorkflowResult.assetSummary.scenes || 0}</span>
                              <span style={{ color: "var(--color-text-primary)" }}>道具 {autoWorkflowResult.assetSummary.props || 0}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {[
                                ["character", "查看角色工坊"],
                                ["scene", "查看场景工坊"],
                                ["prop", "查看道具工坊"],
                              ].map(([assetType, label]) => (
                                <button
                                  key={assetType}
                                  type="button"
                                  onClick={() => window.open(`/project/${projectId}/assets/${assetType}`, "_blank")}
                                  className="px-2.5 py-1 rounded-md border text-sm"
                                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)", backgroundColor: SURFACE.panelSoft }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            {autoWorkflowNeedsConfirmation && (
                              <div className="text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                                确认或调整资产后，点击“确认资产并继续”会把角色参考图入库 GuaVideo，并生成全部小故事板视频。
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={onSearch}
                    disabled={searchDisabled}
                    className="h-11 px-5 rounded-md text-sm font-semibold flex items-center gap-2 border disabled:opacity-50"
                    style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                  >
                    {loading === "search" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                    {config?.textActionLabel || "载入选题"}
                  </button>
                </div>
              </div>
            )}
          </section>

          <aside className="space-y-4">
            <div className="rounded-md border p-5 space-y-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
              <div className="flex items-center gap-2">
                <Target size={17} style={{ color: theme.colors.accentPrimary }} />
                <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>本页目标</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(sourceType === "novel_text" ? ["保留原文", "画面化改写", activeSource.label] : ["多集短剧", "强反转", activeSource.label]).map((item) => (
                  <span key={item} className="px-2.5 py-1 rounded-full text-sm" style={TONE_STYLE.accent}>{item}</span>
                ))}
              </div>
              <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>立项阶段会固定</div>
                <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
                  {sourceType === "novel_text"
                    ? "小说素材、剧本化方向、短剧设定、风格锚点和分集大纲。确认大纲后，分集生产台才会开放。"
                    : "素材、改编方向、短剧设定、风格锚点和分集大纲。确认大纲后，分集生产台才会开放。"}
                </div>
              </div>
              <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>后续生产会落到</div>
                <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
                  剧本专区、角色/场景/道具工坊、分镜列表、图片生成和视频生成。
                </div>
              </div>
            </div>

            <div className="rounded-md border p-5 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
              <div className="flex items-center gap-2">
                <Clock3 size={17} style={{ color: theme.colors.accentPrimary }} />
                <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>最近草稿</div>
              </div>
              {sessions.length ? sessions.slice(0, 4).map((bundle) => {
                const item = bundle.session;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onRefreshSession(item.id)}
                    className="w-full rounded-md border p-3 text-left cursor-pointer"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
                  >
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{item.title || `草稿 ${item.id}`}</div>
                    <div className="text-sm truncate mt-1" style={{ color: "var(--color-text-muted)" }}>{STATUS_LABEL[item.status] || item.status || "草稿"}</div>
                  </button>
                );
              }) : (
                <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>还没有草稿，开始后会自动创建。</div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </FlowShell>
  );
};

const CandidateConfirmPage = ({
  session,
  candidates,
  selectedCandidate,
  selectedCandidateId,
  loading,
  sourceType,
  sourceQuery,
  sourceText,
  onCreateSession,
  onSelectCandidate,
  onBack,
  onSearchAgain,
  onConfirm,
  mockAgentEnabled,
  onToggleMockAgent,
  config,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const selectedSummary = selectedCandidate?.summary || selectedCandidate?.fullText || "请先选择一个素材。";
  const selectedStoryLine = candidateStoryLine(selectedCandidate);
  return (
    <FlowShell activeStep={2} session={session} onCreateSession={onCreateSession} creating={loading === "create"} mockAgentEnabled={mockAgentEnabled} onToggleMockAgent={onToggleMockAgent} config={config}>
      <div className="max-w-[1320px] mx-auto grid grid-cols-[300px_minmax(0,1fr)_300px] gap-5">
        <aside className="rounded-md border p-5 space-y-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div>
            <div className="text-xl font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
              {sourceType === "novel_text" ? "你的小说素材" : "你的搜索需求"}
            </div>
            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
              <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {sourceTypeLabel(sourceType)}
              </div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
                {isTextSourceType(sourceType) ? sourceText.slice(0, 120) : sourceQuery}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ListChecks size={17} style={{ color: theme.colors.accentPrimary }} />
              <div className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>确认方式</div>
            </div>
            <div className="rounded-md border p-3 flex items-center gap-3" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}>
                <Check size={12} />
              </span>
              <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>单选一个素材</span>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="w-full rounded-md border p-3 flex items-center gap-3 cursor-pointer"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-secondary)" }}
            >
              <Compass size={15} />
              <span className="text-sm font-semibold">返回修改方向</span>
            </button>
          </div>

            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
              <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>选择后会发生什么</div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
              确认素材后，会进入项目级导演对话。导演不会自动生成内容，你可以先补充偏好，再发送开始分析。
              </div>
            </div>
        </aside>

        <main className="rounded-md border p-5 space-y-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-2xl font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>{config?.candidateTitle || "确认立项素材"}</div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>
                {config?.candidateDescription || "单选一个素材作为项目源头。确认后进入导演对话，再由你发送要求推进素材理解、改编方案、短剧设定、风格锚点和分集大纲。"}
              </div>
            </div>
            <SmallButton onClick={onSearchAgain} disabled={loading === "search"} icon={loading === "search" ? Loader2 : sourceType === "ai_theme" ? Wand2 : Search}>
              {sourceActionLabel(sourceType, config, true)}
            </SmallButton>
          </div>
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
            {candidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                selected={String(selectedCandidateId) === String(candidate.id)}
                loading={loading === "submitSource"}
                onSelect={(item) => onSelectCandidate(String(item.id))}
              />
            ))}
          </div>
        </main>

        <aside className="rounded-md border p-5 flex flex-col gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div>
            <div className="text-xl font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>{sourceReviewTitle(sourceType)}</div>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>先看它的短剧钩子、连续剧情空间和改编风险。</div>
          </div>
          <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
            <div className="text-base font-semibold leading-6" style={{ color: "var(--color-text-primary)" }}>
              {selectedCandidate?.title || "还未选择"}
            </div>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>{selectedSummary}</div>
          </div>
          {selectedStoryLine && (
            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
              <div className="text-sm font-semibold" style={{ color: theme.colors.accentPrimary }}>一句话故事</div>
              <div className="text-sm leading-6" style={{ color: "var(--color-text-primary)" }}>{selectedStoryLine}</div>
            </div>
          )}
          {selectedCandidate && (
            <div className="space-y-3">
              {[
                ["短剧钩子", score(meta(selectedCandidate, "adaptation_potential", meta(selectedCandidate, "heat_score")))],
                ["奇葩程度", score(meta(selectedCandidate, "oddity_score"))],
                ["传播热度", score(meta(selectedCandidate, "heat_score"))],
              ].map(([label, value]) => (
                <div key={label} className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: "var(--color-text-secondary)" }}>{label}</span>
                    <span style={{ color: "var(--color-text-muted)" }}>{value || 0}/100</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: SURFACE.raised }}>
                    <div className="h-full rounded-full" style={{ width: `${Math.min(value || 0, 100)}%`, backgroundColor: theme.colors.accentPrimary }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-auto space-y-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={!selectedCandidate || loading === "submitSource"}
              className="w-full h-11 rounded-md text-sm font-semibold flex items-center justify-center gap-2 border disabled:opacity-50"
              style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
            >
              {loading === "submitSource" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {config?.confirmButtonLabel || "确认这个素材"}
            </button>
            <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
              确认后停在立项对话，不会自动生成，也不会直接进入单集剧本。
            </div>
          </div>
        </aside>
      </div>
    </FlowShell>
  );
};

const AdaptationAgentWorkbench = ({ projectId, mode = "adaptation", autoMode = "" }) => {
  const navigate = useNavigate();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const config = WORKBENCH_MODES[mode] || WORKBENCH_MODES.adaptation;
  const workflowKey = config.workflowKey;
  const initialMemory = useMemo(() => readAdaptationMemory(projectId, config.memoryNamespace), [projectId, config.memoryNamespace]);
  const [mockAgentEnabled, setMockAgentEnabledState] = useState(() => isMockAgentEnabled());
  const [workflows, setWorkflows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionBundle, setSessionBundle] = useState(null);
  const [sourceType, setSourceType] = useState(initialMemory.sourceType || config.defaultSourceType);
  const [sourceQuery, setSourceQuery] = useState(initialMemory.sourceQuery || config.defaultSourceQuery);
  const [sourceText, setSourceText] = useState(initialMemory.sourceText || "");
  const [candidates, setCandidates] = useState(Array.isArray(initialMemory.candidates) ? initialMemory.candidates : []);
  const [selectedCandidateId, setSelectedCandidateId] = useState(initialMemory.selectedCandidateId || "");
  const [selectedStageKey, setSelectedStageKey] = useState(initialMemory.selectedStageKey || "");
  const [userRequest, setUserRequest] = useState("");
  const [userFeedback, setUserFeedback] = useState("");
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [selectedStyleOption, setSelectedStyleOption] = useState(null);
  const [generatingStyleOptionId, setGeneratingStyleOptionId] = useState("");
  const [activeInspector, setActiveInspector] = useState("board");
  const [promptInfo, setPromptInfo] = useState(null);
  const [promptDraft, setPromptDraft] = useState("");
  const [contextInfo, setContextInfo] = useState(null);
  const [stageConfirmOpen, setStageConfirmOpen] = useState(false);
  const [loading, setLoading] = useState("");
  const [directorProgressText, setDirectorProgressText] = useState("");
  const restoredSessionRef = useRef(false);
  const conversationScrollRef = useRef(null);
  const conversationEndRef = useRef(null);

  const session = sessionBundle?.session;
  const workflow = sessionBundle?.workflow || workflows.find((item) => item.key === workflowKey) || workflows[0];
  const stages = workflow?.stages || [];
  const stage = stages.find((item) => item.key === selectedStageKey) || stages[0];
  const stageRuns = sessionBundle?.stageRuns || [];
  const lastRun = stage ? getRunOutput(stageRuns, stage.key) : null;
  const sourceMaterial = parseMaybeJson(session?.sourceMaterialJson, null);
  const sourceMaterialStoryLine = materialStoryLine(sourceMaterial);
  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => String(candidate.id) === String(selectedCandidateId)) || null,
    [candidates, selectedCandidateId],
  );
  const lastRunOutput = parseMaybeJson(lastRun?.outputJson, null);
  const lastRunOptions = Array.isArray(lastRunOutput?.options) ? lastRunOutput.options : [];
  const selectedStyleRun = selectedStyleOption
    ? stageRuns.find((run) => String(run.id) === String(selectedStyleOption.runId))
    : null;
  const selectedStyleOptions = optionsFromRun(selectedStyleRun);
  const effectiveSelectedOptionId = stage?.key === "style_anchor"
    ? (selectedStyleOption?.optionId || selectedOptionId)
    : selectedOptionId;
  const selectedStyleOptionSourceRunId = stage?.key === "style_anchor" && selectedStyleOption
    ? selectedStyleOption.runId
    : null;
  const selectedStyleFromHistory = Boolean(
    selectedStyleOptionSourceRunId && lastRun?.id && String(selectedStyleOptionSourceRunId) !== String(lastRun.id),
  );
  const selectedOptionLabel = stage?.key === "style_anchor" && selectedStyleOption
    ? optionDisplayName(selectedStyleOptions, selectedStyleOption.optionId)
    : optionDisplayName(lastRunOptions, selectedOptionId);
  const currentStageGuide = stageGuide(stage?.key, stage?.name);
  const currentStageIndex = stage ? stages.findIndex((item) => item.key === stage.key) : -1;
  const previousStage = currentStageIndex > 0 ? stages[currentStageIndex - 1] : null;
  const previousStageRun = previousStage ? getRunOutput(stageRuns, previousStage.key) : null;
  const previousStageOutput = parseMaybeJson(previousStageRun?.outputJson, null);
  const previousConfirmedRunFailed = Boolean(previousStageRun?.status === "CONFIRMED" && runHasError(previousStageRun, previousStageOutput));
  const nextStage = currentStageIndex >= 0 ? stages[currentStageIndex + 1] : null;
  const nextStageGuide = stageGuide(nextStage?.key, nextStage?.name);
  const isConfirmingStage = Boolean(stage && loading === `confirm-${stage.key}`);
  const artifacts = sessionBundle?.artifacts || [];
  const productionReady = artifacts.some((artifact) => artifact.artifactType === "EPISODE_OUTLINE" && artifact.selected === true);
  const episodeCompletion = stage?.key === "episode_script"
    ? episodeScriptCompletion(lastRunOutput, artifacts)
    : { complete: true, expectedCount: 0, readyCount: 0, missingNumbers: [], message: "" };

  useEffect(() => {
    const syncMockAgent = () => setMockAgentEnabledState(isMockAgentEnabled());
    window.addEventListener("storage", syncMockAgent);
    window.addEventListener("ai-provider-change", syncMockAgent);
    return () => {
      window.removeEventListener("storage", syncMockAgent);
      window.removeEventListener("ai-provider-change", syncMockAgent);
    };
  }, []);

  useEffect(() => {
    const memory = readAdaptationMemory(projectId, config.memoryNamespace);
    restoredSessionRef.current = false;
    setSessions([]);
    setSessionBundle(null);
    setSourceType(memory.sourceType || config.defaultSourceType);
    setSourceQuery(memory.sourceQuery || config.defaultSourceQuery);
    setSourceText(memory.sourceText || "");
    setCandidates(Array.isArray(memory.candidates) ? memory.candidates : []);
    setSelectedCandidateId(memory.selectedCandidateId || "");
    setSelectedStageKey(memory.selectedStageKey || "");
  }, [projectId, config.memoryNamespace, config.defaultSourceType, config.defaultSourceQuery]);

  const refreshSession = useCallback(async (id) => {
    if (!id) return;
    const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions/${id}`);
    setSessionBundle(data);
    const nextStage = data?.session?.currentStageKey || data?.workflow?.stages?.[0]?.key || "";
    setSelectedStageKey(nextStage);
    if (data?.session?.id) {
      writeAdaptationMemory(projectId, {
        activeSessionId: data.session.id,
        selectedStageKey: nextStage,
      }, config.memoryNamespace);
    }
    return data;
  }, [projectId, config.memoryNamespace]);

  const loadWorkflows = useCallback(async () => {
    const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/workflows`);
    setWorkflows(data || []);
  }, []);

  const loadSessions = useCallback(async () => {
    const suffix = projectId ? `?projectId=${projectId}` : "";
    const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions${suffix}`);
    const nextSessions = (data || []).filter((bundle) => bundle?.session?.workflowKey === workflowKey);
    setSessions(nextSessions);
    return nextSessions;
  }, [projectId, workflowKey]);

  useEffect(() => {
    let mounted = true;
    loadWorkflows().catch((error) => {
      if (!mounted) return;
      console.error("[AdaptationAgent] 加载工作流失败:", error);
      toast.error(`${config.title || "创作工作台"}加载失败，请看后端日志`);
    });
    loadSessions().catch((error) => {
      if (!mounted) return;
      console.error("[AdaptationAgent] 加载草稿失败:", error);
      toast.error(`${config.title || "创作工作台"}草稿加载失败，请确认数据库迁移已执行`);
    });
    return () => { mounted = false; };
  }, [config.title, loadWorkflows, loadSessions, toast]);

  useEffect(() => {
    writeAdaptationMemory(projectId, {
      sourceType,
      sourceQuery,
      sourceText: sourceType === "novel_text" ? "" : trimForMemory(sourceText),
      candidates: candidatesForMemory(sourceType, candidates),
      selectedCandidateId,
    }, config.memoryNamespace);
  }, [projectId, config.memoryNamespace, sourceType, sourceQuery, sourceText, candidates, selectedCandidateId]);

  useEffect(() => {
    if (!session?.id) return;
    writeAdaptationMemory(projectId, {
      activeSessionId: session.id,
      selectedStageKey: selectedStageKey || session.currentStageKey || "",
    }, config.memoryNamespace);
  }, [projectId, config.memoryNamespace, session?.id, session?.currentStageKey, selectedStageKey]);

  useEffect(() => {
    if (restoredSessionRef.current || sessionBundle || !sessions.length) return;
    const memory = readAdaptationMemory(projectId, config.memoryNamespace);
    const rememberedId = memory.activeSessionId;
    const rememberedExists = rememberedId && sessions.some((bundle) => String(bundle?.session?.id) === String(rememberedId));
    const sessionIdToRestore = rememberedExists ? rememberedId : sessions[0]?.session?.id;
    if (!sessionIdToRestore) return;
    restoredSessionRef.current = true;
    refreshSession(sessionIdToRestore).catch((error) => {
      console.error("[AdaptationAgent] 恢复草稿失败:", error);
    });
  }, [projectId, config.memoryNamespace, refreshSession, sessionBundle, sessions]);

  useEffect(() => {
    setPromptInfo(null);
    setPromptDraft("");
    setContextInfo(null);
    setSelectedOptionId("");
    setSelectedStyleOption(null);
  }, [session?.id, selectedStageKey]);

  const createSessionBundle = async () => {
    const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions`, {
      method: "POST",
      body: JSON.stringify({
        projectId,
        workflowKey,
        sourceType,
        title: sourceDraftTitle(sourceType, sourceQuery, sourceText),
      }),
    });
    setSessionBundle(data);
    const nextStageKey = data?.session?.currentStageKey || data?.workflow?.stages?.[0]?.key || "";
    setSelectedStageKey(nextStageKey);
    if (data?.session?.id) {
      writeAdaptationMemory(projectId, {
        activeSessionId: data.session.id,
        selectedStageKey: nextStageKey,
      }, config.memoryNamespace);
    }
    await loadSessions();
    return data;
  };

  const createSession = async () => {
    setLoading("create");
    try {
      await createSessionBundle();
      toast.success("已创建改编流程");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const toggleMockAgent = () => {
    const nextEnabled = !mockAgentEnabled;
    setMockAgentEnabled(nextEnabled);
    setMockAgentEnabledState(nextEnabled);
    toast.success(nextEnabled ? "已开启演示模式，本页会使用示例内容" : "已关闭演示模式，恢复正式生成");
  };

  const searchSource = async () => {
    if (isTextSourceType(sourceType) && !sourceText.trim()) {
      toast.warning(sourceType === "novel_text" ? "请先粘贴小说内容" : "请先输入素材内容");
      return;
    }
    setLoading("search");
    try {
      let activeSession = session;
      if (!activeSession) {
        const bundle = await createSessionBundle();
        activeSession = bundle.session;
      }
      const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions/${activeSession.id}/source/search`, {
        method: "POST",
        body: JSON.stringify({
          sourceType,
          query: sourceQuery,
          text: sourceText,
        }),
      });
      const nextCandidates = data || [];
      setCandidates(nextCandidates);
      setSelectedCandidateId(nextCandidates[0]?.id ? String(nextCandidates[0].id) : "");
      await refreshSession(activeSession.id);
      await loadSessions();
      const hasLive = nextCandidates.some((candidate) => ["live_search", "model_web_search"].includes(meta(candidate, "provider_status")));
      if (sourceType === "hot_news" && !hasLive) {
        toast.warning("暂未找到可用实时热点，当前显示备用候选");
      } else {
        toast.success(sourceType === "hot_news" ? "已搜索热点事件" : sourceType === "ai_theme" ? "已生成题材候选" : sourceType === "novel_text" ? "已载入小说素材" : "已载入文本素材");
      }
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const submitSelectedCandidate = async () => {
    if (!selectedCandidate) {
      toast.warning("请先选择一个素材");
      return;
    }
    setLoading("submitSource");
    try {
      let activeSession = session;
      if (!activeSession) {
        const bundle = await createSessionBundle();
        activeSession = bundle.session;
      }
      const selectedBundle = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions/${activeSession.id}/source/select`, {
        method: "POST",
        body: JSON.stringify({ candidate: selectedCandidate }),
      });
      setSessionBundle(selectedBundle);
      const nextStageKey = selectedBundle?.session?.currentStageKey || selectedBundle?.workflow?.stages?.[0]?.key || "source_understanding";
      setSelectedStageKey(nextStageKey);
      setUserRequest(config.defaultAnalysisRequest || DEFAULT_SOURCE_ANALYSIS_REQUEST);
      setUserFeedback("");
      setSelectedOptionId("");
      setSelectedStyleOption(null);
      await refreshSession(activeSession.id);
      setContextInfo(null);
      setActiveInspector("board");
      toast.success("素材已确认。已进入导演对话，请发送要求后再开始分析。");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const runStage = async () => {
    if (!session || !stage) return;
    setLoading(`run-${stage.key}`);
    setDirectorProgressText("正在提交给导演");
    try {
      const { result: data } = await runTaskToCompletion("ADAPTATION_AGENT_RUN_STAGE", {
        sessionId: session.id,
        stageKey: stage.key,
        stagePayload: {
          userRequest,
          userFeedback,
          selectedOptionId: effectiveSelectedOptionId,
          selectedOptionSourceRunId: selectedStyleOptionSourceRunId,
        },
        projectId: session.projectId || session.project_id || null,
      }, {
        projectId: session.projectId || session.project_id || null,
        source: "adaptation_agent_stage",
        timeoutMs: 30 * 60 * 1000,
        onProgress: (status) => {
          setDirectorProgressText(firstText(status?.currentStageText, status?.stageText, status?.status, "导演正在处理"));
        },
      });
      await refreshSession(session.id);
      setUserRequest("");
      setUserFeedback("");
      setSelectedOptionId("");
      setSelectedStyleOption(null);
      setContextInfo(data?.contextSnapshot || null);
      setActiveInspector("board");
      toast.success("导演已完成这一轮思考");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
      setDirectorProgressText("");
    }
  };

  const requestStyleOptionImage = async (optionId) => {
    const { result } = await runTaskToCompletion("ADAPTATION_STYLE_ANCHOR_OPTION_IMAGE", {
      sessionId: session.id,
      stageKey: stage.key,
      optionId,
    }, {
      projectId: session.projectId || session.project_id || null,
      source: "adaptation_style_anchor_option_image",
      timeoutMs: 30 * 60 * 1000,
    });
    return result;
  };

  const generateStyleOptionImage = async (optionId) => {
    if (!session || !stage || !optionId) return;
    setGeneratingStyleOptionId(String(optionId));
    try {
      const data = await requestStyleOptionImage(optionId);
      await refreshSession(session.id);
      if (data?.option?.image_generation_status === "failed") {
        toast.warning("概念图生成失败，可以稍后重试或换一个候选");
      } else {
        toast.success("概念图已生成");
      }
    } catch (error) {
      await refreshSession(session.id).catch(() => {});
      toast.error(error.message, 0);
    } finally {
      setGeneratingStyleOptionId("");
    }
  };

  const generateAllStyleOptionImages = async () => {
    if (!session || !stage) return;
    const pendingOptions = lastRunOptions.filter((option) => !firstText(option.image_url, option.selected_image_url) && option.image_prompt);
    if (!pendingOptions.length) return;
    let successCount = 0;
    let failedCount = 0;
    try {
      for (const option of pendingOptions) {
        setGeneratingStyleOptionId(String(option.id));
        try {
          const data = await requestStyleOptionImage(option.id);
          if (data?.option?.image_generation_status === "failed") {
            failedCount += 1;
          } else {
            successCount += 1;
          }
          await refreshSession(session.id);
        } catch {
          failedCount += 1;
          await refreshSession(session.id).catch(() => {});
        }
      }
      if (failedCount) {
        toast.warning(`概念图生成完成 ${successCount} 张，失败 ${failedCount} 张，可单独重试失败项`);
      } else {
        toast.success("全部概念图已生成");
      }
    } finally {
      setGeneratingStyleOptionId("");
    }
  };

  const canOpenStageConfirm = () => {
    if (!session || !stage) return;
    if (currentRunFailed) {
      toast.warning("当前阶段生成失败，不能确认进入下一步。请重新生成成功后再确认。");
      return false;
    }
    if (!currentRunCanConfirm) {
      toast.warning("当前阶段还没有可确认的成功结果");
      return false;
    }
    if (lastRunOptions.length && !effectiveSelectedOptionId) {
      toast.warning("请先单选一个方案，再提交进入下一步");
      return false;
    }
    if (stage.key === "episode_script" && !episodeScriptHasAnyReady) {
      toast.warning("请先生成至少一集完整剧本，再进入后续流程。");
      return false;
    }
    return true;
  };

  const openStageConfirmDialog = () => {
    if (!canOpenStageConfirm()) return;
    setStageConfirmOpen(true);
  };

  const confirmStage = async () => {
    if (!canOpenStageConfirm()) return;
    setStageConfirmOpen(false);
    setActiveInspector("board");
    setLoading(`confirm-${stage.key}`);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions/${session.id}/stages/${stage.key}/confirm`, {
        method: "POST",
        body: JSON.stringify({
          selectedOptionId: effectiveSelectedOptionId,
          selectedOptionSourceRunId: selectedStyleOptionSourceRunId,
          userFeedback,
        }),
      });
      setSessionBundle(data);
      const nextStageKey = data?.session?.currentStageKey || stage.key;
      const nextStage = data?.workflow?.stages?.find((item) => item.key === nextStageKey);
      const nextGuide = stageGuide(nextStageKey, nextStage?.name);
      setSelectedStageKey(nextStageKey);
      setUserRequest("");
      setUserFeedback("");
      setSelectedOptionId("");
      setSelectedStyleOption(null);
      setActiveInspector("board");
      window.dispatchEvent(new CustomEvent("adaptation-production-state-change", { detail: { projectId } }));
      if (stage.key === "asset_breakdown" && data?.autoPublishedAssets) {
        const assets = data.autoPublishedAssets;
        const reusedTotal = (assets.characterReused || 0) + (assets.sceneReused || 0) + (assets.propReused || 0);
        toast.success(
          `资产已自动同步到工坊：新增角色 ${assets.characterCreated || 0}，场景 ${assets.sceneCreated || 0}，道具 ${assets.propCreated || 0}；复用已有 ${reusedTotal} 个。下一步：${nextGuide.title}`
        );
      } else if (stage.key === "asset_breakdown" && data?.autoPublishAssetsError) {
        toast.warning(`资产自动同步失败：${data.autoPublishAssetsError}。下一步：${nextGuide.title}`);
      } else if (!nextStage && stage.key === "episode_outline") {
        toast.success("立项完成。现在可以进入分集生产台，按集写剧本、做分镜和视频提示词。");
      } else if (!nextStage) {
        toast.success("已确认，当前工作流已完成。");
      } else {
        toast.success(`已确认，下一步：${nextGuide.title}`);
      }
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const loadPrompt = async () => {
    if (!workflow || !stage) return;
    setLoading("prompt");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/prompts/${workflow.key}/${stage.key}?projectId=${projectId || ""}`);
      setPromptInfo(data);
      setPromptDraft(data?.effectivePrompt || "");
      setActiveInspector("prompt");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const savePromptOverride = async () => {
    if (!workflow || !stage) return;
    setLoading("savePrompt");
    try {
      await authenticatedFetch(`${API_HOST}/api/adaptation-agent/prompt-overrides`, {
        method: "POST",
        body: JSON.stringify({
          projectId,
          workflowKey: workflow.key,
          stageKey: stage.key,
          promptContent: promptDraft,
          enabled: true,
        }),
      });
      setPromptInfo((current) => current ? { ...current, effectivePrompt: promptDraft } : current);
      toast.success("已保存当前阶段创作规则");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const loadContext = async () => {
    if (!session || !stage) return;
    setLoading("context");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/adaptation-agent/sessions/${session.id}/stages/${stage.key}/context`, {
        method: "POST",
        body: JSON.stringify({ userRequest, userFeedback, selectedOptionId }),
      });
      setContextInfo(data);
      setActiveInspector("context");
    } catch (error) {
      toast.error(error.message, 0);
    } finally {
      setLoading("");
    }
  };

  const chatRuns = stageRuns.filter((run) => run.outputJson || run.userRequest || run.userFeedback);
  const selectedStageRuns = stage ? stageRuns.filter((run) => run.stageKey === stage.key) : [];
  const selectedStageArtifacts = artifacts.filter((artifact) => artifact.stageKey === stage?.key);
  const currentRunCanConfirm = runCanConfirm(lastRun, lastRunOutput);
  const currentRunFailed = runHasError(lastRun, lastRunOutput);
  const episodeScriptHasAnyReady = stage?.key === "episode_script" && episodeCompletion.readyCount > 0;
  const stageCanConfirm = stage?.key !== "episode_script" || episodeScriptHasAnyReady;
  const episodeConfirmBlocked = stage?.key === "episode_script" && currentRunCanConfirm && !episodeScriptHasAnyReady;
  const episodeScriptPartialNotice = stage?.key === "episode_script" && currentRunCanConfirm && episodeScriptHasAnyReady && !episodeCompletion.complete;
  const episodeScriptNeedsMore = stage?.key === "episode_script" && currentRunCanConfirm && !episodeCompletion.complete;
  const forceSourceGuide = autoMode === "explainer" || autoMode === "dialogue";
  const composerIsSource = forceSourceGuide || !sourceMaterial;
  const directorConversationActive = Boolean(sourceMaterial);
  const composerValue = composerIsSource
    ? (isTextSourceType(sourceType) ? sourceText : sourceQuery)
    : (currentRunCanConfirm ? userFeedback : userRequest);
  const composerPlaceholder = composerIsSource
    ? (sourceType === "hot_news"
      ? "输入你想找的奇葩热点，比如：偷别人河豚回家吃了中毒"
      : sourceType === "ai_theme"
        ? "输入一个主题，AI 会发散多个短剧题材，比如：底层外卖员误入豪门秘密"
        : (config?.textPlaceholder || "粘贴小说、梗概、原作设定或任意素材。"))
    : (episodeScriptNeedsMore
      ? "直接点击生成下一集，或指定：生成3集 / 第5-7集 / 修改第4集 / 追加第21集"
      : currentRunCanConfirm
      ? (stage?.key === "style_anchor"
        ? "可以继续要求：更像90年代港片 / 更破败县城 / 更国漫厚涂 / 光影再冷一点"
        : "像聊天一样告诉导演：我选这个方向，但主角改成外卖员 / 节奏再快一点 / 重想一个更狠的反转")
      : currentStageGuide.prompt);
	  const primaryActionLabel = composerIsSource
	    ? (candidates.length && selectedCandidate ? "确认素材，进入导演对话" : sourceActionLabel(sourceType, config))
	    : (episodeScriptNeedsMore && !userFeedback.trim()
      ? "生成下一集"
      : currentRunCanConfirm
      ? (effectiveSelectedOptionId && !userFeedback.trim() ? "准备进入下一步" : "发送给导演优化")
      : "发送给导演");
  const primaryActionLoading = loading === "search" || loading === "submitSource" || loading.startsWith("run-") || loading.startsWith("confirm-");
  const stageChatDisabled = !session || !stage || primaryActionLoading || (currentRunCanConfirm && !userFeedback.trim() && !effectiveSelectedOptionId && !episodeScriptNeedsMore);
  const confirmActionDisabled = !session || !stage || primaryActionLoading || !currentRunCanConfirm || !stageCanConfirm || (lastRunOptions.length > 0 && !effectiveSelectedOptionId);
  const primaryActionDisabled = composerIsSource
    ? (loading === "search" || loading === "submitSource" || (candidates.length > 0 && !selectedCandidate))
    : stageChatDisabled;
  const directorThinking = !composerIsSource && (loading.startsWith("run-") || loading.startsWith("confirm-"));
  useEffect(() => {
    if (!sourceMaterial) return undefined;
    const timer = window.setTimeout(() => {
      conversationEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [
    sourceMaterial,
    chatRuns.length,
    lastRun?.id,
    loading,
    selectedStageKey,
    generatingStyleOptionId,
    candidates.length,
  ]);

  const runPrimaryAction = () => {
    if (composerIsSource) {
      if (candidates.length && selectedCandidate) {
        submitSelectedCandidate();
      } else {
        searchSource();
      }
      return;
    }
    if (currentRunCanConfirm && effectiveSelectedOptionId && !userFeedback.trim()) {
      openStageConfirmDialog();
      return;
    }
    runStage();
  };

  const openProductionWorkbench = useCallback(() => {
    navigate(`/project/${projectId}/adaptation-agent/episodes`);
  }, [navigate, projectId]);

  if (!sourceMaterial || forceSourceGuide) {
    if (!forceSourceGuide && candidates.length > 0) {
      return (
        <CandidateConfirmPage
          session={session}
          candidates={candidates}
          selectedCandidate={selectedCandidate}
          selectedCandidateId={selectedCandidateId}
          loading={loading}
          sourceType={sourceType}
          sourceQuery={sourceQuery}
          sourceText={sourceText}
          onCreateSession={createSession}
          onSelectCandidate={setSelectedCandidateId}
          onBack={() => {
            setCandidates([]);
            setSelectedCandidateId("");
          }}
          onSearchAgain={searchSource}
          onConfirm={submitSelectedCandidate}
          mockAgentEnabled={mockAgentEnabled}
          onToggleMockAgent={toggleMockAgent}
          config={config}
        />
      );
    }

    return (
      <SourceGuidePage
        projectId={projectId}
        session={session}
        sessions={sessions}
        sourceType={sourceType}
        sourceQuery={sourceQuery}
        sourceText={sourceText}
        loading={loading}
        onCreateSession={createSession}
        onRefreshSession={refreshSession}
        onSearch={searchSource}
        onSourceTypeChange={(nextType) => {
          setSourceType(nextType);
          setCandidates([]);
          setSelectedCandidateId("");
        }}
        onSourceQueryChange={(value) => {
          setSourceQuery(value);
          setCandidates([]);
          setSelectedCandidateId("");
        }}
        onSourceTextChange={(value) => {
          setSourceText(value);
          setCandidates([]);
          setSelectedCandidateId("");
        }}
        mockAgentEnabled={mockAgentEnabled}
        onToggleMockAgent={toggleMockAgent}
        config={config}
        autoMode={autoMode}
      />
    );
  }

  return (
    <div className="w-full h-full overflow-hidden flex flex-col" style={{ backgroundColor: SURFACE.page }}>
      <AgentMotionStyles />
      <StageConfirmModal
        open={stageConfirmOpen}
        currentStage={stage}
        nextStage={nextStage}
        guide={nextStageGuide}
        selectedLabel={selectedOptionLabel}
        selectedFromHistory={selectedStyleFromHistory}
        loading={loading.startsWith("confirm-")}
        onCancel={() => setStageConfirmOpen(false)}
        onConfirm={confirmStage}
      />
      <header className="h-16 shrink-0 border-b px-4 flex items-center gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
        <div className="min-w-[220px] flex items-center gap-3">
          <span className="w-9 h-9 rounded-md flex items-center justify-center" style={TONE_STYLE.muted}>
            <Bot size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-base font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{config.title}</div>
            <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
              {session ? `草稿 ${session.id} · ${productionReady ? "已立项" : directorConversationActive ? "立项对话中" : STATUS_LABEL[session.status] || "进行中"}` : config.subtitle}
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="h-9 rounded-md border px-3 flex items-center gap-2 max-w-[520px]" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
            <Sparkles size={14} style={{ color: theme.colors.accentPrimary }} />
            <span
              className="text-sm px-1.5 py-0.5 rounded shrink-0"
              style={{
                ...(directorConversationActive ? TONE_STYLE.info : TONE_STYLE.accent),
              }}
            >
              {productionReady ? "已立项" : directorConversationActive ? "立项对话" : "选题中"}
            </span>
            <span className="text-base font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{stage?.name || "等待开始"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SmallButton onClick={toggleMockAgent} icon={Bot} active={mockAgentEnabled} title="使用示例数据快速体验">
            {mockAgentEnabled ? "演示中" : "演示模式"}
          </SmallButton>
          <SmallButton onClick={createSession} disabled={loading === "create"} icon={loading === "create" ? Loader2 : RefreshCw}>
            新建草稿
          </SmallButton>
          {productionReady && (
            <SmallButton onClick={openProductionWorkbench} icon={Workflow} active>
              分集生产台
            </SmallButton>
          )}
          <SmallButton onClick={loadPrompt} disabled={!stage || loading === "prompt"} icon={loading === "prompt" ? Loader2 : Settings2}>
            创作规则
          </SmallButton>
          <SmallButton onClick={loadContext} disabled={!session || !stage || loading === "context"} icon={loading === "context" ? Loader2 : Database}>
            参考资料
          </SmallButton>
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-[320px_minmax(0,1fr)_360px]">
        <aside className="min-h-0 border-r overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <section className="space-y-2">
            <PanelTitle icon={Layers} title="立项步骤" />
            <LaunchProgressPanel
              stages={stages}
              currentKey={stage?.key}
              stageRuns={stageRuns}
              productionReady={productionReady}
              onSelectStage={setSelectedStageKey}
              onOpenProduction={openProductionWorkbench}
            />
          </section>

          <section className="space-y-2">
            <PanelTitle icon={FileText} title="已定素材" />
            {sourceMaterial ? (
              <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-success)", backgroundColor: "var(--color-green-light)" }}>
                <div className="text-sm font-semibold leading-5" style={{ color: "var(--color-text-primary)" }}>{sourceMaterial.title}</div>
                {sourceMaterialStoryLine && (
                  <div className="rounded-md px-2.5 py-2" style={{ backgroundColor: "var(--color-accent-light)" }}>
                    <div className="text-sm font-semibold mb-1" style={{ color: "var(--color-accent-primary)" }}>一句话故事</div>
                    <div className="text-sm leading-6" style={{ color: "var(--color-text-primary)" }}>{sourceMaterialStoryLine}</div>
                  </div>
                )}
                <div className="text-sm leading-6 line-clamp-5" style={{ color: "var(--color-text-secondary)" }}>
                  {sourceMaterial.summary || sourceMaterial.fullText}
                </div>
              </div>
	            ) : (
	              <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panelSoft }}>
	                素材确认后，会固定在这里，后续创作都会围绕它展开。
	              </div>
	            )}
          </section>

          <section className="space-y-2">
            <PanelTitle icon={ClipboardList} title="立项物料" />
            <ArtifactList artifacts={artifacts} stages={stages} onSelectStage={setSelectedStageKey} />
          </section>

          <section className="space-y-2">
            <PanelTitle icon={Clock3} title="草稿记录" />
            {sessions.length === 0 ? (
              <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>还没有草稿，开始后会自动创建。</div>
            ) : sessions.slice(0, 4).map((bundle) => {
              const item = bundle.session;
              const active = session?.id === item?.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => refreshSession(item.id)}
                  className="w-full rounded-md border px-2.5 py-2 text-left cursor-pointer"
                  style={{
                    borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                    backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panelSoft,
                  }}
                >
                  <div className="text-sm font-semibold truncate" style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-primary)" }}>
                    {item.title || `草稿 ${item.id}`}
                  </div>
                  <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
                    {stages.find((stageItem) => stageItem.key === item.currentStageKey)?.name || "未开始"} · {STATUS_LABEL[item.status] || "草稿"}
                  </div>
                </button>
              );
            })}
          </section>
        </aside>

        <main className="min-w-0 min-h-0 flex flex-col" style={{ backgroundColor: SURFACE.page }}>
          <div ref={conversationScrollRef} className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-4">
            <ChatMessage role="assistant" title="导演助手" subtitle="准备就绪">
              {config.chatIntro}
            </ChatMessage>

            {sourceMaterial && (
              <>
	                <ChatMessage role="user" title={config.selectedSourceTitle || "已确认素材"} subtitle={sourceTypeLabel(sourceMaterial.sourceType)}>
                  <div className="font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{sourceMaterial.title}</div>
                  {sourceMaterialStoryLine && (
                    <div className="mb-2 rounded-md px-3 py-2" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-text-primary)" }}>
                      <div className="font-semibold mb-1" style={{ color: "var(--color-accent-primary)" }}>一句话故事</div>
                      <div>{sourceMaterialStoryLine}</div>
                    </div>
                  )}
                  <div>{sourceMaterial.summary || sourceMaterial.fullText}</div>
                </ChatMessage>
                <ChatMessage role="assistant" title="立项对话已开启" subtitle={stage?.name || "创作推进"}>
                  接下来只推进项目级内容。你可以选择方案、提出修改点、要求重想；确认分集大纲后，系统会开放分集生产台。
                </ChatMessage>
              </>
            )}

            {productionReady && (
              <LaunchCompletePanel onOpenProduction={openProductionWorkbench} />
            )}

            {isConfirmingStage && (
              <StageHandoffPanel
                stage={nextStage || stage}
                guide={nextStage ? nextStageGuide : currentStageGuide}
                previousStage={stage}
                confirming
              />
            )}

            {candidates.length > 0 && !sourceMaterial && (
	              <ChatMessage role="assistant" title={sourceType === "hot_news" ? "热点搜索结果" : sourceType === "ai_theme" ? "题材候选" : "素材候选"} subtitle={`${candidates.length} 条`}>
	                <div>{sourceType === "novel_text" ? "小说素材已载入。请选择它，再提交进入创作分析。" : "我找到了这些候选素材。请选择一个，再提交进入创作分析。"}</div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 mt-3">
                  {candidates.map((candidate) => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      active={sourceMaterial?.title === candidate.title}
                      selected={String(selectedCandidateId) === String(candidate.id)}
                      loading={loading === "submitSource"}
                      onSelect={(item) => setSelectedCandidateId(String(item.id))}
                    />
                  ))}
                </div>
              </ChatMessage>
            )}

            {chatRuns.map((run) => {
              const runStage = stages.find((item) => item.key === run.stageKey);
              const runOutput = parseMaybeJson(run.outputJson, null);
              const runOptions = Array.isArray(runOutput?.options) ? runOutput.options : [];
              const isCurrentRun = stage?.key === run.stageKey && lastRun?.id === run.id;
              const isStyleAnchorRun = runStage?.key === "style_anchor";
              const canDecide = isCurrentRun && runCanConfirm(run, runOutput);
              const canSelectHistoricalStyle = isStyleAnchorRun && stage?.key === "style_anchor" && currentRunCanConfirm && runCanConfirm(run, runOutput);
              const displaySelectedOptionId = isStyleAnchorRun
                ? (String(selectedStyleOption?.runId) === String(run.id) ? selectedStyleOption.optionId : run.selectedOptionId)
                : (canDecide ? selectedOptionId : run.selectedOptionId);
              const handleRunOptionSelect = (optionId) => {
                if (isStyleAnchorRun) {
                  setSelectedStyleOption({ runId: run.id, optionId: String(optionId) });
                  setSelectedOptionId(isCurrentRun ? String(optionId) : "");
                  return;
                }
                setSelectedOptionId(String(optionId));
              };
              const runSelectedOptionLabel = optionDisplayName(runOptions, run.selectedOptionId);
              return (
                <React.Fragment key={run.id}>
                  {(run.userRequest || run.userFeedback || run.selectedOptionId) && (
                    <ChatMessage role="user" title="你的指令" subtitle={runStage?.name || "当前阶段"}>
                      {run.userRequest && <div>{run.userRequest}</div>}
                      {run.selectedOptionId && <div className="mt-1">选择：{runSelectedOptionLabel}</div>}
                      {run.userFeedback && <div className="mt-1">补充：{run.userFeedback}</div>}
                    </ChatMessage>
                  )}
                  <ChatMessage role="assistant" title={runStage?.name || "当前阶段"} subtitle={STATUS_LABEL[run.status] || "进行中"}>
                    {runOutput?.error ? (
                      <div className="space-y-2">
                        <div style={{ color: "var(--color-error)" }}>{runOutput.error}</div>
                        {isCurrentRun && (
                          <div className="rounded-md border px-3 py-2 text-sm leading-6" style={{ borderColor: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.1)", color: "var(--color-text-secondary)" }}>
                            本轮生成失败，不能确认进入下一步。请在底部重新发送，等生成成功后再确认。
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div>
	                          {runStage?.key === "style_anchor"
                            ? "我给你准备了几组风格候选。可以先逐张生成概念图，再单选一张确认；也可以在底部补充偏好后重新生成。"
                            : runOptions.length
                              ? "我给你准备了几个方向。你可以选一个继续让导演优化，也可以确认后进入下一步。"
                              : "这一阶段的产物已生成。你可以继续提修改意见，也可以确认后进入下一步。"}
                        </div>
                        <OptionCards
                          options={runOptions}
                          selectedOptionId={displaySelectedOptionId}
                          onSelect={handleRunOptionSelect}
                          disabled={isStyleAnchorRun ? !canSelectHistoricalStyle : !canDecide}
                          onGenerateImage={canDecide && runStage?.key === "style_anchor" ? generateStyleOptionImage : undefined}
                          onGenerateAllImages={canDecide && runStage?.key === "style_anchor" ? generateAllStyleOptionImages : undefined}
                          generatingOptionId={generatingStyleOptionId}
                        />
                        {runStage?.key === "style_anchor" && <StyleAnchorFixedSummary output={runOutput} />}
                        {!runOptions.length && <VisualOutput output={runOutput} />}
                        {canDecide && (
                          <div className="mt-3 space-y-2 text-sm">
                            {(episodeConfirmBlocked || episodeScriptPartialNotice) && (
                              <div className="rounded-md border px-3 py-2" style={{ borderColor: "var(--color-warning)", backgroundColor: "rgba(212,167,68,0.12)", color: "var(--color-text-primary)" }}>
                                {episodeConfirmBlocked
                                  ? "请先生成至少一集完整剧本，再进入后续流程。"
                                  : `${episodeCompletion.message} 也可以先进入后续流程，之后再回到这里继续写或修改。`}
                              </div>
                            )}
                            <div style={{ color: "var(--color-text-muted)" }}>
                              {episodeConfirmBlocked
                                ? "可以在底部先生成下一集。"
                                : episodeScriptPartialNotice
                                  ? "发送会继续写下一集；确认按钮会先带着当前已有剧本进入后续流程。"
                                  : "发送会继续优化当前阶段；确认按钮才会推进到下一步。"}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </ChatMessage>
                </React.Fragment>
              );
            })}

	            {session && stage && !lastRun && !sourceMaterial && (
	              <div className="animate-in fade-in duration-300">
	                <ChatMessage role="assistant" title={stage.name} subtitle="下一步">
	                  {config.sourceComposerHint || "请先输入热点需求或素材内容。素材确认后会进入导演对话，由你发送第一条要求后再开始分析。"}
	                </ChatMessage>
	              </div>
	            )}

            {session && stage && !lastRun && sourceMaterial && previousConfirmedRunFailed && (
              <div className="animate-in fade-in duration-300">
                <StageErrorGatePanel
                  stage={previousStage}
                  output={previousStageOutput}
                  onBack={() => setSelectedStageKey(previousStage.key)}
                />
              </div>
            )}

            {session && stage && !lastRun && sourceMaterial && !previousConfirmedRunFailed && (
              <div className="animate-in fade-in duration-300">
                <StageHandoffPanel stage={stage} guide={currentStageGuide} previousStage={previousStage} />
              </div>
            )}
            <div ref={conversationEndRef} aria-hidden="true" />
          </div>

          <div className="shrink-0 border-t p-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
            <div
              className="relative rounded-md border p-3 space-y-3 overflow-hidden"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}
              aria-busy={directorThinking}
            >
              {directorThinking && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center px-4 backdrop-blur-sm animate-in fade-in duration-150"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--color-bg-overlay) 84%, transparent)",
                    color: "var(--color-text-primary)",
                  }}
                >
                  <div
                    className="rounded-md border px-4 py-3 flex items-center gap-3 shadow-lg"
                    style={{
                      borderColor: "var(--color-border-primary)",
                      backgroundColor: "var(--color-bg-card)",
                    }}
                  >
                    <Loader2 size={18} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
                    <div>
                      <div className="text-sm font-semibold">{loading.startsWith("confirm-") ? "正在确认并准备下一步" : "导演正在思考中"}</div>
                      <div className="text-sm mt-0.5" style={{ color: "var(--color-text-muted)" }}>
                        {loading.startsWith("confirm-") ? "正在保存选择和物料版本，请稍等。" : firstText(directorProgressText, "正在整理你的要求，请稍等。")}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles size={14} style={{ color: theme.colors.accentPrimary }} />
                  <span className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                    {composerIsSource ? (config.firstInputLabel || "第一步：选题输入") : "和立项导演继续聊"}
                  </span>
                  {!composerIsSource && (
                    <span className="text-sm px-1.5 py-0.5 rounded" style={TONE_STYLE.info}>
                      当前：{stage?.name || "创作推进"}
                    </span>
                  )}
                  {effectiveSelectedOptionId && (
                    <span className="text-sm px-1.5 py-0.5 rounded" style={TONE_STYLE.info}>
                      已选 {selectedStyleFromHistory ? "历史图：" : ""}{selectedOptionLabel || "方案"}
                    </span>
                  )}
                </div>
                {composerIsSource && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {(config.sourceTypes || SOURCE_TYPES).map((source) => (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => {
                          setSourceType(source.id);
                          setCandidates([]);
                          setSelectedCandidateId("");
                        }}
                        className="h-8 px-2.5 rounded-md text-sm border flex items-center gap-1 cursor-pointer"
                        style={{
                          borderColor: sourceType === source.id ? theme.colors.accentPrimary : "var(--color-border-primary)",
                          backgroundColor: sourceType === source.id ? "var(--color-accent-light)" : SURFACE.panelSoft,
                          color: sourceType === source.id ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                        }}
                      >
                        <source.icon size={12} />
                        {source.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <textarea
                value={composerValue}
                onChange={(e) => {
                  if (composerIsSource) {
                    if (isTextSourceType(sourceType)) setSourceText(e.target.value);
                    else setSourceQuery(e.target.value);
                  } else if (currentRunCanConfirm) {
                    setUserFeedback(e.target.value);
                  } else {
                    setUserRequest(e.target.value);
                  }
                }}
                rows={composerIsSource && isTextSourceType(sourceType) ? 5 : 3}
                className="w-full p-2.5 rounded-md text-sm outline-none resize-none border"
                style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft, color: "var(--color-text-primary)" }}
                placeholder={composerPlaceholder}
                disabled={directorThinking}
              />

              <div className="flex items-center justify-between gap-2">
                <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
                  {composerIsSource
                    ? (config.sourceComposerHint || "先查找或载入选题；确认后进入导演对话，由你发送要求再开始。")
                    : currentRunFailed
                      ? "当前阶段生成失败，不能确认进入下一步。请调整要求后重新发送。"
                      : currentRunCanConfirm
                      ? episodeConfirmBlocked
                        ? "请先生成至少一集完整剧本，再进入后续流程。"
                        : episodeScriptPartialNotice
                          ? "当前剧本还可以继续往下写；也可以先确认进入后续流程，之后再回来补写或修改。"
                        : effectiveSelectedOptionId && !userFeedback.trim()
                          ? "已选择方案，点击会先确认是否进入下一步；输入修改意见后发送则继续优化当前阶段。"
                          : "发送会让导演在当前阶段继续优化；只有确认后才会推进流程。"
                      : `导演还没有开始处理。${currentStageGuide.cta}`}
                </div>
                <div className="flex items-center gap-2 shrink-0">
	                  {composerIsSource && candidates.length > 0 && (
	                    <SmallButton onClick={searchSource} disabled={loading === "search"} icon={loading === "search" ? Loader2 : Search}>
	                      {sourceActionLabel(sourceType, config, true)}
	                    </SmallButton>
	                  )}
                  <button
                    type="button"
                    onClick={runPrimaryAction}
                    disabled={primaryActionDisabled}
                    className="h-9 px-4 rounded-md text-sm font-semibold flex items-center gap-2 border cursor-pointer disabled:opacity-50"
                    style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
                  >
                    {primaryActionLoading && !loading.startsWith("confirm-") ? <Loader2 size={14} className="animate-spin" /> : composerIsSource && !candidates.length ? <Search size={14} /> : <Send size={14} />}
                    {primaryActionLabel}
                  </button>
                  {!composerIsSource && currentRunCanConfirm && (
                    <button
                      type="button"
                      onClick={openStageConfirmDialog}
                      disabled={confirmActionDisabled}
                      title={episodeConfirmBlocked ? "请先生成至少一集完整剧本" : undefined}
                      className="h-9 px-4 rounded-md text-sm font-semibold flex items-center gap-2 border cursor-pointer disabled:opacity-50"
                      style={{
                        borderColor: theme.colors.accentPrimary,
                        backgroundColor: "var(--color-accent-light)",
                        color: theme.colors.accentPrimary,
                      }}
                    >
                      {loading.startsWith("confirm-") ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      {loading.startsWith("confirm-") ? "正在确认并归档" : "确认并进入下一步"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <aside className="min-h-0 border-l flex flex-col" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div className="h-12 px-3 border-b flex items-center gap-1.5" style={{ borderColor: "var(--color-border-primary)" }}>
            <PanelRight size={14} style={{ color: theme.colors.accentPrimary }} />
            {[
              ["board", "立项板"],
              ["prompt", "创作规则"],
              ["context", "参考资料"],
            ].map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveInspector(tab)}
                className="h-8 px-2.5 rounded-md text-sm cursor-pointer"
                style={{
                  backgroundColor: activeInspector === tab ? "var(--color-accent-light)" : "transparent",
                  color: activeInspector === tab ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {activeInspector === "prompt" ? (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="shrink-0 px-3 py-2 border-b flex items-center justify-between gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                    {promptInfo ? `${stage?.name || "当前阶段"} · 本项目规则` : "当前阶段创作规则"}
                  </div>
                  <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
                    这些规则会影响当前阶段的创作方式，保存后只作用于本项目。
                  </div>
                </div>
                <SmallButton onClick={savePromptOverride} disabled={!promptInfo || loading === "savePrompt"} icon={loading === "savePrompt" ? Loader2 : Check} active>
                  保存
                </SmallButton>
              </div>
              <textarea
                value={promptDraft}
                onChange={(e) => setPromptDraft(e.target.value)}
                className="flex-1 min-h-0 resize-none outline-none p-3 text-sm leading-6 font-mono"
                style={{ color: "var(--color-text-secondary)", backgroundColor: "transparent" }}
                placeholder="点击顶部“创作规则”加载当前阶段规则。"
              />
              {promptInfo?.defaultPrompt && promptDraft !== promptInfo.defaultPrompt && (
                <div className="shrink-0 px-3 py-2 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
                  <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>当前规则已调整</span>
                  <SmallButton onClick={() => setPromptDraft(promptInfo.defaultPrompt)}>恢复默认规则</SmallButton>
                </div>
              )}
            </div>
          ) : activeInspector === "context" ? (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-3">
              <PanelTitle
                icon={Database}
                title="当前阶段参考资料"
                action={(
                  <SmallButton onClick={loadContext} disabled={!session || !stage || loading === "context"} icon={loading === "context" ? Loader2 : RefreshCw}>
                    刷新
                  </SmallButton>
                )}
              />
              {contextInfo ? (
                <ReadableValue value={contextInfo.finalContext || contextInfo.packets || contextInfo} />
              ) : (
                <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panelSoft }}>
                  点击刷新后查看本阶段会参考的素材、选择和历史内容。
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-4">
              <section className="space-y-2">
                <PanelTitle icon={Sparkles} title="当前立项焦点" />
                <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
                  <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {previousConfirmedRunFailed ? "上一阶段失败" : isConfirmingStage ? "正在阶段交接" : directorConversationActive && !lastRun ? "等待你的输入" : directorConversationActive ? "导演对话中" : "等待选题"}
                  </div>
                  <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
                    {previousConfirmedRunFailed
                      ? `「${previousStage?.name || "上一阶段"}」返回了错误结果，请先回到该阶段重新生成。`
                      : isConfirmingStage
                      ? `正在确认「${stage?.name || "当前阶段"}」，下一步会进入「${nextStage?.name || "下一阶段"}」。`
                      : directorConversationActive && !lastRun
                        ? `下一步是「${stage?.name || "创作推进"}」。立项导演还没有开始处理，请在底部输入要求并发送。`
                        : directorConversationActive
                      ? `当前聊到：${stage?.name || "创作推进"}`
                      : "先在中间底部输入热点或素材需求"}
                  </div>
                </div>
              </section>

              {(previousConfirmedRunFailed || isConfirmingStage || (sourceMaterial && stage && !lastRun)) && (
                <section className="space-y-2">
                  <PanelTitle icon={Compass} title="阶段指引" />
                  <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded flex items-center justify-center" style={previousConfirmedRunFailed ? { backgroundColor: "rgba(220,38,38,0.14)", color: "var(--color-error)" } : isConfirmingStage ? TONE_STYLE.accent : TONE_STYLE.info}>
                        {previousConfirmedRunFailed ? "!" : isConfirmingStage ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      </span>
                      <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                        {previousConfirmedRunFailed ? "先修复失败阶段" : isConfirmingStage ? "确认内容并准备下一步" : "等待你发送下一步要求"}
                      </div>
                    </div>
                    <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
                      {previousConfirmedRunFailed
                        ? "当前不能继续下一步。回到出错阶段重新生成成功后，再确认推进。"
                        : isConfirmingStage
                        ? "确认期间按钮会锁定，完成后页面会停在下一阶段，不会自动生成。"
                        : "底部输入框才是触发入口。你可以直接发送，也可以补充节奏、字数、风格等要求。"}
                    </div>
                  </div>
                </section>
              )}

              {sourceMaterial && (
                <section className="space-y-2">
                  <PanelTitle icon={FileText} title="素材摘要" />
                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panelSoft }}>
                    <div className="text-sm font-semibold leading-5" style={{ color: "var(--color-text-primary)" }}>{sourceMaterial.title}</div>
                    {sourceMaterialStoryLine && (
                      <div className="rounded-md px-2.5 py-2" style={{ backgroundColor: "var(--color-accent-light)" }}>
                        <div className="text-sm font-semibold mb-1" style={{ color: "var(--color-accent-primary)" }}>一句话故事</div>
                        <div className="text-sm leading-6" style={{ color: "var(--color-text-primary)" }}>{sourceMaterialStoryLine}</div>
                      </div>
                    )}
                    <div className="text-sm leading-6" style={{ color: "var(--color-text-secondary)" }}>{sourceMaterial.summary || sourceMaterial.fullText}</div>
                  </div>
                </section>
              )}

              <section className="space-y-2">
                <PanelTitle icon={ClipboardList} title="本阶段产物" />
                {selectedStageRuns.length ? selectedStageRuns.slice(-2).reverse().map((run) => {
                  const output = parseMaybeJson(run.outputJson, null);
                  return (
                    <div key={run.id} className="space-y-2">
                      <div className="text-sm" style={{ color: "var(--color-text-muted)" }}>{STATUS_LABEL[run.status] || "进行中"}</div>
                      <VisualOutput output={output} />
                    </div>
                  );
                }) : (
                  <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panelSoft }}>
                    当前阶段还没有生成内容。
                  </div>
                )}
              </section>

              {!!selectedStageArtifacts.length && (
                <section className="space-y-2">
                  <PanelTitle icon={Layers} title="已沉淀物料" />
                  <ArtifactList artifacts={selectedStageArtifacts} stages={stages} onSelectStage={setSelectedStageKey} />
                </section>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AdaptationAgentWorkbench;
